/** Pipeline Topologie - Sender/Receiver Verbindungsansicht (Integration in One Redesign)
 *  Alle Funktionen der Vorversion erhalten: Space/Instanz-Auswahl, Status-Filter,
 *  Sortierung, Focus-Modus, Spalten-Suche, Szenarien-Filter, Verbindungslinien,
 *  Detail-Drawer (Übersicht / Pipeline Steps / Partner Directory / Monitoring),
 *  Deep Search, PD-Edit, KI-Fehleranalyse, Retry, Headers/Attachments, Fit-to-View. */
import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import MuiButton from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import MuiCircularProgress from "@mui/material/CircularProgress";
import MuiDrawer from "@mui/material/Drawer";
import MuiDialog from "@mui/material/Dialog";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import MuiTextField from "@mui/material/TextField";
import MuiTooltip from "@mui/material/Tooltip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LaunchIcon from "@mui/icons-material/Launch";
import { FONT } from "../theme/tokens";
import { api } from "../utils/helpers";
import { emitEvent } from "../store/eventBridge";
import { useApiQuery } from "../hooks/useApiQuery";

// Platzhalter fuer Szenarien ohne gepflegtes Sender- bzw. Receiver-System.
// Diese Zeichenketten sind WERTE: sie bilden die Kennung der Karte ("ss_…",
// "rs_…") und gehen in die Deep Search. Sie bleiben deshalb woertlich und
// wechseln nicht mit der Sprache. Angezeigt wird statt dessen der Katalogtext.
const WERT_OHNE_SENDER = "(unbekannt)";
const WERT_OHNE_RECEIVER = "(kein Receiver)";

// ============================================================
// Freshness-Indikator - nur sichtbar wenn experimentalCaching aktiv
// ============================================================

function FreshnessIndicator({ dataUpdatedAt }) {
  const { t } = useTranslation(["pipeline", "gemeinsam"]);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!dataUpdatedAt) return;
    // Zaehlvariable bewusst nicht "t": das wuerde die Uebersetzungsfunktion verdecken.
    const id = setInterval(() => setTick(n => n + 1), 5000);
    return () => clearInterval(id);
  }, [dataUpdatedAt]);
  if (!dataUpdatedAt) return null;
  const ageMs = Date.now() - dataUpdatedAt;
  const ageSec = Math.round(ageMs / 1000);

  let color = "var(--iio-success)";
  let label = t("frische.sekunden", { n: ageSec });
  if (ageSec > 300) { color = "var(--iio-critical)"; label = t("frische.minuten", { n: Math.round(ageSec / 60) }); }
  else if (ageSec > 60) { color = "var(--iio-warning)"; label = t("frische.minuten", { n: Math.round(ageSec / 60) }); }

  return (
    <span style={{ fontSize: 11, color, opacity: 0.85, marginLeft: 6 }} title={t("frische.titel", { alter: label })}>
      {label}
    </span>
  );
}

// ============================================================
// Toolbar
// ============================================================


function Toolbar({
  activeFilter, setActiveFilter,
  sortMode, setSortMode,
  spaces, selectedSpace, setSelectedSpace,
  spaceInstances, selectedInst, setSelectedInst,
  focusMode, setFocusMode,
  showUnused, setShowUnused,
  scopeHours, setScopeHours,
  loading,
}) {
  const { t } = useTranslation(["pipeline", "gemeinsam"]);
  const LOCK_MSG = t("leiste.gesperrt");
  const Lock = ({ children }) => loading
    ? <MuiTooltip title={LOCK_MSG} arrow placement="top"><span style={{ display: "inline-flex" }}>{children}</span></MuiTooltip>
    : children;

  return (
    <div className="iio-pip-toolbar">
      {/* 1. Space-Auswahl */}
      <Lock>
      <select className="iio-pip-sel" value={selectedSpace?.id || ""} disabled={loading}
        onChange={(e) => { const sp = spaces.find(s => s.id === e.target.value); if (sp) setSelectedSpace(sp); }}>
        <option value="" disabled>{t("leiste.spaceWaehlen")}</option>
        {(spaces || []).map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
      </select>
      </Lock>

      {/* 2. Instanz-Auswahl (gefiltert nach Space) */}
      <Lock>
      <select className="iio-pip-sel" value={selectedInst?.id || ""} disabled={!selectedSpace || loading}
        onChange={(e) => { const inst = spaceInstances.find(i => i.id === e.target.value); if (inst) setSelectedInst(inst); }}>
        <option value="" disabled>{t("leiste.instanzWaehlen")}</option>
        {(spaceInstances || []).map((inst) => <option key={inst.id} value={inst.id}>{inst.name}</option>)}
      </select>
      </Lock>

      <div className="iio-pip-tdiv" />

      {/* Status-Filter */}
      <Lock>
      <select className="iio-pip-sel" value={activeFilter} disabled={loading} onChange={(e) => setActiveFilter(e.target.value)}>
        <option value="all">{t("leiste.statusAlle")}</option>
        <option value="active">{t("leiste.statusAktiv")}</option>
        <option value="error">{t("leiste.statusFehler")}</option>
        <option value="inactive">{t("leiste.statusInaktiv")}</option>
      </select>
      </Lock>

      {/* Sortierung */}
      <Lock>
      <select className="iio-pip-sel" value={sortMode} disabled={loading} onChange={(e) => setSortMode(e.target.value)}>
        <option value="alpha">{t("leiste.sortAlpha")}</option>
        <option value="connections">{t("leiste.sortSzenarien")}</option>
        <option value="status">{t("leiste.sortFehlerrate")}</option>
      </select>
      </Lock>

      {/* Zeitraum der Szenario-Suche */}
      <Lock>
      <select className="iio-pip-sel" value={scopeHours} disabled={loading} onChange={(e) => setScopeHours(Number(e.target.value))}>
        <option value={1}>{t("leiste.zeit1")}</option>
        <option value={6}>{t("leiste.zeit6")}</option>
        <option value={24}>{t("leiste.zeit24")}</option>
        <option value={72}>{t("leiste.zeit72")}</option>
        <option value={168}>{t("leiste.zeit168")}</option>
        <option value={720}>{t("leiste.zeit720")}</option>
      </select>
      </Lock>

      <div style={{ flex: 1 }} />

      {/* Focus Mode */}
      <Lock>
      <label className="iio-pip-fok" style={loading ? { opacity: .45, pointerEvents: "none" } : undefined}>
        <input type="checkbox" checked={focusMode} disabled={loading} onChange={(e) => setFocusMode(e.target.checked)} />
        <span className="trk" />
        <span className="flbl">{t("leiste.focus")}</span>
      </label>
      </Lock>

      {/* Nicht genutzte Pipelines (im Partner Directory konfiguriert, aber 0 Nachrichten in 24h) */}
      <Lock>
      <MuiTooltip title={t("leiste.ungenutztTitel")} arrow placement="top">
      <label className="iio-pip-fok" style={loading ? { opacity: .45, pointerEvents: "none" } : undefined}>
        <input type="checkbox" checked={showUnused} disabled={loading} onChange={(e) => setShowUnused(e.target.checked)} />
        <span className="trk" />
        <span className="flbl">{t("leiste.ungenutzt")}</span>
      </label>
      </MuiTooltip>
      </Lock>
    </div>
  );
}

// ============================================================
// Spalten-Ansicht
// ============================================================

function ColumnsView({
  senderSystems, scenarios, receiverSystems,
  onCardHover, onCardHighlight,
  onScenarioClick,
  focusMode, focusedCard, setFocusedCard,
  columnSearch, setColumnSearch,
  stats, scopeLabel = "24 h",
}) {
  const { t, i18n } = useTranslation(["pipeline", "gemeinsam"]);
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const isConnectedTo = useCallback((cardId, cardType, activeId, activeType) => {
    if (!activeId || !scenarios?.length) return false;
    if (cardId === activeId) return true;
    return scenarios.some(sc => {
      if (activeType === "sender" && cardType === "scenario")
        return sc.senderSystemId === activeId && sc.id === cardId;
      if (activeType === "sender" && cardType === "receiver")
        return sc.senderSystemId === activeId && sc.receiverSystemId === cardId;
      if (activeType === "scenario" && cardType === "sender")
        return sc.id === activeId && sc.senderSystemId === cardId;
      if (activeType === "scenario" && cardType === "receiver")
        return sc.id === activeId && sc.receiverSystemId === cardId;
      if (activeType === "receiver" && cardType === "scenario")
        return sc.receiverSystemId === activeId && sc.id === cardId;
      if (activeType === "receiver" && cardType === "sender")
        return sc.receiverSystemId === activeId && sc.senderSystemId === cardId;
      return false;
    });
  }, [scenarios]);

  const isCardFocusVisible = useCallback((item, type) => {
    if (!focusMode || !focusedCard) return true;
    if (focusedCard.id === item.id && focusedCard.type === type) return true;
    return isConnectedTo(item.id, type, focusedCard.id, focusedCard.type);
  }, [focusMode, focusedCard, isConnectedTo]);

  const [scenarioSort, setScenarioSort] = useState("alpha");
  const [showScenarioFilter, setShowScenarioFilter] = useState(false);
  const filterRef = useRef(null);
  useEffect(() => {
    if (!showScenarioFilter) return;
    const handler = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowScenarioFilter(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showScenarioFilter]);

  const scenarioMsgCounts = useMemo(() => {
    const pidCounts = stats?.pidCounts || {};
    const counts = {};
    scenarios.forEach(sc => {
      if (sc._total != null) { counts[sc.id] = { total: sc._total, errors: sc._failed || 0 }; return; }
      const pc = pidCounts[sc.pid];
      counts[sc.id] = pc ? { total: pc.total, errors: pc.failed } : { total: 0, errors: 0 };
    });
    return counts;
  }, [scenarios, stats]);

  // Aggregierte 24h-Zahlen pro System (fuer die Zahlen auf den System-Karten)
  const systemAgg = useMemo(() => {
    const m = {};
    scenarios.forEach(sc => {
      const c = scenarioMsgCounts[sc.id] || { total: 0, errors: 0 };
      [sc.senderSystemId, sc.receiverSystemId].forEach(id => {
        if (!id) return;
        if (!m[id]) m[id] = { total: 0, errors: 0 };
        m[id].total += c.total;
        m[id].errors += c.errors;
      });
    });
    return m;
  }, [scenarios, scenarioMsgCounts]);

  const sortedScenarios = useMemo(() => {
    let list = [...scenarios];
    if (scenarioSort === "throughput") list.sort((a, b) => (scenarioMsgCounts[b.id]?.total || 0) - (scenarioMsgCounts[a.id]?.total || 0));
    else if (scenarioSort === "errors") list.sort((a, b) => (scenarioMsgCounts[b.id]?.errors || 0) - (scenarioMsgCounts[a.id]?.errors || 0));
    else if (scenarioSort === "uncertain") list = list.filter(sc => !sc.senderCertain);
    return list;
  }, [scenarios, scenarioSort, scenarioMsgCounts]);

  const fmt = (n) => (n || 0).toLocaleString(gebiet);

  const handleCardClick = (e, item, type) => {
    e.stopPropagation();
    if (focusMode) setFocusedCard({ id: item.id, type });
    else onCardHighlight({ id: item.id, type });
  };

  const renderSystemCard = (item, type, idx) => {
    const count = item.scenarioCount || 0;
    const visible = isCardFocusVisible(item, type);
    const agg = systemAgg[item.id] || { total: 0, errors: 0 };
    const led = !stats ? "" : agg.errors > 0 ? " err" : agg.total === 0 ? " idle" : "";
    return (
      <div key={item.id} className={`iio-pip-wrap${visible ? "" : " hide"}`}>
        <div
          className="iio-pip-card"
          style={{ animationDelay: `${Math.min(idx * 35, 420)}ms` }}
          onMouseEnter={() => onCardHover({ id: item.id, type })}
          onMouseLeave={() => onCardHover(null)}
          onClick={(e) => handleCardClick(e, item, type)}
          data-card-id={item.id}
          data-card-type={type}
        >
          <span className={`iio-pip-led${led}`} />
          <div className="iio-pip-cmain">
            <span className="iio-pip-cname">{item.label}</span>
            <span className="iio-pip-csub">{t("spalten.szenarien", { count })}</span>
            {type === "sender" && Array.isArray(item.aliases) && item.aliases.length > 0 && (
              <span
                style={{ display: "block", fontSize: 10.5, color: "var(--iio-signal)", marginTop: 1, cursor: "help" }}
                title={t("spalten.aliasTitel", { count: item.aliases.length, system: item.name, aliase: item.aliases.join(", ") })}
              >
                {t("spalten.inkl", { liste: item.aliases.slice(0, 2).join(", ") + (item.aliases.length > 2 ? ` +${item.aliases.length - 2}` : "") })}
              </span>
            )}
          </div>
          <span className="iio-pip-cnums">
            <span className="nv">{fmt(agg.total)}</span>
            <span className="nl">{t("spalten.msg24h")}</span>
          </span>
        </div>
      </div>
    );
  };

  const questionIcon = (
    <MuiTooltip title={t("spalten.unsicherTitel")} arrow placement="top">
      <span className="iio-pip-unc">?</span>
    </MuiTooltip>
  );

  const renderScenarioCard = (sc, idx) => {
    const visible = isCardFocusVisible(sc, "scenario");
    const c = scenarioMsgCounts[sc.id] || { total: 0, errors: 0 };
    const led = !stats ? "" : c.errors > 0 ? " err" : c.total === 0 ? " idle" : "";
    return (
      <div key={sc.id} className={`iio-pip-wrap${visible ? "" : " hide"}`}>
        <div
          className="iio-pip-card"
          style={{ animationDelay: `${Math.min(idx * 35, 420)}ms` }}
          onMouseEnter={() => onCardHover({ id: sc.id, type: "scenario" })}
          onMouseLeave={() => onCardHover(null)}
          onClick={(e) => {
            handleCardClick(e, sc, "scenario");
            if (onScenarioClick) onScenarioClick(sc);
          }}
          data-card-id={sc.id}
          data-card-type="scenario"
        >
          <span className={`iio-pip-led${led}`} />
          <div className="iio-pip-cmain">
            <span className="iio-pip-cname">{sc.label}{!sc.senderCertain && questionIcon}</span>
            <span className="iio-pip-croute">
              <span className="rseg">{sc.senderSystemLabel || sc.senderSystemName || t("spalten.senderUnbekannt")}</span>
              <i className="iio-pip-chv" /><i className="iio-pip-chv" />
              <span className="rseg">{sc.receiverSystemLabel || sc.receiverSystemName || "?"}</span>
            </span>
            {sc.senderRuntimeAlias && sc.senderRuntimeAlias !== sc.senderSystemName && (
              <span
                style={{ display: "block", fontSize: 10.5, color: "var(--iio-text-3)", marginTop: 2, cursor: "help" }}
                title={t("spalten.aliasSzenarioTitel", {
                  alias: sc.senderRuntimeAlias,
                  system: sc.senderSystemName,
                  umgebung: sc.senderEnv ? t("spalten.umgebung", { env: sc.senderEnv }) : "",
                })}
              >
                {t("spalten.ueberAlias", { alias: sc.senderRuntimeAlias })}{sc.senderEnv ? ` (${sc.senderEnv})` : ""}
              </span>
            )}
          </div>
          {/* Zwei gleich hohe Haelften: oben alle Prozessdurchlaeufe, darunter die
              fehlerhaften. Beide Zahlen fuehren in die Prozessansicht des Szenarios.
              Die Zusatzklasse haelt das von den Sender- und Receiver-Karten fern. */}
          <span className="iio-pip-cnums sz">
            {c.total > 0 ? (
              <>
                <MuiTooltip arrow placement="top" title={t("spalten.durchlaeufeTitel", { zeitraum: scopeLabel })}>
                  <span className="nv" role="button" tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); if (onScenarioClick) onScenarioClick(sc, { tab: 3 }); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); if (onScenarioClick) onScenarioClick(sc, { tab: 3 }); } }}>
                    {fmt(c.total)}
                  </span>
                </MuiTooltip>
                {c.errors > 0
                  ? <MuiTooltip arrow placement="bottom" title={t("spalten.fehlerTitel", { count: c.errors, zahl: fmt(c.errors) })}>
                      <span className="ne" role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); if (onScenarioClick) onScenarioClick(sc, { tab: 3, nurFehler: true }); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); if (onScenarioClick) onScenarioClick(sc, { tab: 3, nurFehler: true }); } }}>
                        {t("spalten.fehlerZahl", { count: c.errors, zahl: fmt(c.errors) })}
                      </span>
                    </MuiTooltip>
                  : <span className="nl">{t("spalten.keineFehler")}</span>}
              </>
            ) : (
              <span className="nl" style={{ fontSize: 9.5 }}>{stats ? t("spalten.inaktiv") : ""}</span>
            )}
          </span>
        </div>
      </div>
    );
  };

  const [activeColSearch, setActiveColSearch] = useState(null);

  const columnLabel = (text, count, colKey, extraIcon) => {
    const isOpen = activeColSearch === colKey;
    const hasValue = !!(columnSearch[colKey] || "").trim();
    return (
      <div style={{ position: "relative" }}>
        <div className={`iio-pip-colh${isOpen || hasValue ? " on" : ""}`}>
          <span className="ct" onClick={() => setActiveColSearch(prev => prev === colKey ? null : colKey)}>
            {text} <span className="cnt">({count})</span>
          </span>
          {extraIcon}
          <svg className="lupe" onClick={() => setActiveColSearch(prev => prev === colKey ? null : colKey)}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ opacity: isOpen || hasValue ? 1 : undefined }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
        <div className={`iio-pip-srchbox${isOpen ? " open" : ""}`}>
          <input
            type="text"
            placeholder={t("spalten.suchen", { was: text })}
            value={columnSearch[colKey] || ""}
            onChange={(e) => setColumnSearch(prev => ({ ...prev, [colKey]: e.target.value }))}
            ref={(el) => { if (el && isOpen) setTimeout(() => el.focus(), 260); }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                setColumnSearch(prev => ({ ...prev, [colKey]: "" }));
                setActiveColSearch(null);
              }
            }}
          />
        </div>
      </div>
    );
  };

  const emptyCol = (text) => (
    <div className="iio-pip-emptycol">{text}</div>
  );

  // Der Schluessel steuert die Sortierung und bleibt woertlich, die Beschriftung
  // kommt aus dem Katalog. Beides war vorher derselbe deutsche Text.
  const filterOptions = [
    { key: "alpha", label: t("spalten.filterAlpha") },
    { key: "throughput", label: t("spalten.filterDurchlaeufe") },
    { key: "errors", label: t("spalten.filterFehlerrate") },
    { key: "uncertain", label: t("spalten.filterUnsicher") },
  ];

  const scenarioFilterIcon = (
    <div ref={filterRef} style={{ position: "relative", display: "inline-flex" }}>
      <svg
        className={`fil${showScenarioFilter || scenarioSort !== "alpha" ? " on" : ""}`}
        onClick={() => setShowScenarioFilter(prev => !prev)}
        width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
      </svg>
      {showScenarioFilter && (
        <div className="iio-pip-fdrop">
          {filterOptions.map(opt => (
            <div
              key={opt.key}
              className={`fo${scenarioSort === opt.key ? " on" : ""}`}
              onClick={() => { setScenarioSort(opt.key); setShowScenarioFilter(false); }}
            >
              <span>{opt.label}</span>
              {opt.key === "throughput" && scenarioMsgCounts && (
                <span className="fm">{fmt(Object.values(scenarioMsgCounts).reduce((s, c) => s + c.total, 0))} MSG</span>
              )}
              {opt.key === "errors" && scenarioMsgCounts && (
                <span className="fm crit">{fmt(Object.values(scenarioMsgCounts).reduce((s, c) => s + c.errors, 0))} ERR</span>
              )}
              {opt.key === "uncertain" && (
                <span className="fm">{scenarios.filter(sc => !sc.senderCertain).length}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="iio-pip-cols">
      <div className="iio-pip-col-s">
        {columnLabel(t("spalten.kopfSender"), senderSystems.length, "sender")}
        <div className="iio-pip-cardlist">
          {senderSystems.length === 0 ? emptyCol(t("spalten.leerSender")) : senderSystems.map((s, i) => renderSystemCard(s, "sender", i))}
        </div>
      </div>
      <div className="iio-pip-col-m">
        {columnLabel(
          t("spalten.kopfSzenarien"),
          scenarioSort === "uncertain" ? `${sortedScenarios.length}/${scenarios.length}` : scenarios.length,
          "scenario",
          scenarioFilterIcon,
        )}
        <div className="iio-pip-cardlist">
          {sortedScenarios.length === 0 ? emptyCol(t("spalten.leerSzenarien")) : sortedScenarios.map((sc, i) => renderScenarioCard(sc, i))}
        </div>
      </div>
      <div className="iio-pip-col-s">
        {columnLabel(t("spalten.kopfReceiver"), receiverSystems.length, "receiver")}
        <div className="iio-pip-cardlist">
          {receiverSystems.length === 0 ? emptyCol(t("spalten.leerReceiver")) : receiverSystems.map((r, i) => renderSystemCard(r, "receiver", i))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Fusszeile
// ============================================================

function Footer({ stats, connections }) {
  const { t } = useTranslation(["pipeline", "gemeinsam"]);
  const ready = !!stats;
  const pidCounts = stats?.pidCounts || {};
  let active = 0, errors = 0, inactive = 0;
  (connections || []).forEach(sc => {
    const total = sc._total != null ? sc._total : (pidCounts[sc.pid]?.total || 0);
    const failed = sc._total != null ? (sc._failed || 0) : (pidCounts[sc.pid]?.failed || 0);
    if (total === 0) inactive++;
    else if (failed > 0) errors++;
    else active++;
  });
  const total = connections?.length || 1;
  const successRate = Math.round((active / total) * 100);

  const helpText = (
    <div style={{ fontSize: 12, lineHeight: 1.6, maxWidth: 360, padding: "2px 0" }}>
      <b>{t("fuss.hilfeTitel")}</b><br />
      {t("fuss.hilfeAbs1")}<br /><br />
      {t("fuss.hilfeAbs2")}<br /><br />
      <b>{t("fuss.hilfeAktivLabel")}</b>{t("fuss.hilfeAktivText")}<br />
      <b>{t("fuss.hilfeUngenutztLabel")}</b>{t("fuss.hilfeUngenutztText")}
    </div>
  );
  return (
    <div className="iio-pip-footer">
      <div className="fl">
        <MuiTooltip title={helpText} arrow placement="top">
          <span className="iio-pip-help" aria-label={t("fuss.erklaerung")}>?</span>
        </MuiTooltip>
        <span>{t("fuss.aktiveSzenarien")} <b>{ready ? active : "-"}</b></span>
        <span>{t("fuss.fehler")} <b className="crit">{ready ? errors : "-"}</b></span>
        <span>{t("fuss.inaktiv")} <b>{ready ? inactive : "-"}</b></span>
      </div>
      <div className="fr">
        <span>{t("fuss.successRate")}</span>
        <div className="bar"><i style={{ width: ready ? `${successRate}%` : 0 }} /></div>
        <b className="ok">{ready ? `${successRate}%` : "-"}</b>
      </div>
    </div>
  );
}

// ============================================================
// Detail-Drawer
// ============================================================

function DetailPanel({ connection, topology, stats, scopeLabel = "24 h", scopeHours = 24, onClose, instanceId, selectedInst, onNavigate, selectedSpace, startTab = 0, startNurFehler = false }) {
  const { t, i18n } = useTranslation(["pipeline", "gemeinsam"]);
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const zahl = (x) => (x ?? 0).toLocaleString(gebiet);
  const [activeTab, setActiveTab] = useState(startTab);
  const [fetchedMsgs, setFetchedMsgs] = useState(null);
  // Korrelierte Prozesse: eine Zeile je fachlichem Lauf statt je Hop.
  const [runs, setRuns] = useState(null);
  const [runsStale, setRunsStale] = useState(15);
  const [runsKey, setRunsKey] = useState(null); // Nachverfolgbarkeit des Szenarios
  const [beteiligte, setBeteiligte] = useState(null); // iFlows aus den korrelierten Laeufen
  const [openRun, setOpenRun] = useState(null);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [deepSearchLoading, setDeepSearchLoading] = useState(false);
  const [deepSearchResults, setDeepSearchResults] = useState(null);
  const [pdEdits, setPdEdits] = useState({});
  const [pdConfirmOpen, setPdConfirmOpen] = useState(false);
  const [pdSaving, setPdSaving] = useState(false);
  const [pdSaveResult, setPdSaveResult] = useState(null);
  const [monErrorDetail, setMonErrorDetail] = useState(null);
  const [monAi, setMonAi] = useState({});
  const [monAiLoading, setMonAiLoading] = useState(null);
  const tabs = [t("panel.reiter.uebersicht"), t("panel.reiter.schritte"), t("panel.reiter.pd"), t("panel.reiter.monitoring")];

  const tenantUrl = topology?.tenantUrl;
  const pid = connection?.pid || "-";
  // Anzeige zuerst: bei Szenarien ohne gepflegtes System traegt senderSystemName
  // den woertlichen Platzhalter, der in Kennung und Deep Search steckt.
  const senderName = connection?.senderSystemLabel || connection?.senderSystemName || t("panel.unbekannt");
  const receiverName = connection?.receiverSystemLabel || connection?.receiverSystemName || t("panel.unbekannt");
  const stringParams = connection?.stringParams || [];
  const altPartners = connection?.altPartners || [];

  const scenarioMessages = useMemo(() => {
    // On-demand nachgeladene Szenario-Nachrichten haben Vorrang (Worker liefert nur Aggregate).
    if (Array.isArray(fetchedMsgs)) return fetchedMsgs;
    if (!connection?.pid || !stats?.pidCounts) return [];
    const pc = stats.pidCounts[connection.pid];
    return pc?.messages || [];
  }, [fetchedMsgs, stats, connection]);

  // Beim Wechsel des Szenarios den Nachladestand zuruecksetzen.
  useEffect(() => { setFetchedMsgs(null); setRuns(null); setOpenRun(null); setOpenStation(null); setBeteiligte(null); }, [connection?.id]);

  // Nachrichten dieses Szenarios laden, sobald der Monitoring-Tab geoeffnet wird
  // (gezielter MPL-Filter ueber Sender + ApplicationMessageType, primaer Fehler).
  useEffect(() => {
    // Auch fuer den Reiter "Pipeline Steps" laden: die beteiligten iFlows stammen aus den
    // korrelierten Laeufen, nicht aus der PD-Konfiguration. Vorher wurden sie nur geholt, wenn
    // der Nutzer zuvor im Monitoring war.
    if ((activeTab !== 3 && activeTab !== 1) || fetchedMsgs !== null || msgsLoading || !instanceId) return;
    const sender = connection?.senderRaw;
    const iface = connection?.senderInterface;
    if (connection?.idle || (!sender && !iface)) { setFetchedMsgs([]); return; }
    setMsgsLoading(true);
    const qs = new URLSearchParams();
    if (sender) qs.set("sender", sender);
    if (iface) qs.set("iface", iface);
    qs.set("hours", String(scopeHours || 24));
    qs.set("limit", "50");
    api(`/api/${instanceId}/pipeline-topology/scenario-runs?${qs.toString()}`)
      .then(d => {
        const r = Array.isArray(d?.runs) ? d.runs : [];
        setRuns(r);
        if (d?.staleMin) setRunsStale(d.staleMin);
        setRunsKey(d?.schluesselQuote == null ? null : { quote: d.schluesselQuote, basis: d.schluesselBasis });
        setBeteiligte(Array.isArray(d?.beteiligteFlows) ? d.beteiligteFlows : []);
        // Flache Hop-Liste weiterhin bereitstellen: "Verwendete iFlows" und der Kopfstatus
        // stuetzen sich darauf. Die ANZEIGE nutzt sie nicht mehr.
        setFetchedMsgs(r.flatMap(x => x.hops.map(h => ({
          id: h.guid, status: h.status, start: h.start, flow: h.flow,
          receiver: h.receiver, correlationId: x.cid,
        }))));
      })
      .catch(() => { setRuns([]); setFetchedMsgs([]); })
      .finally(() => setMsgsLoading(false));
  }, [activeTab, connection, fetchedMsgs, msgsLoading, instanceId, scopeHours]);

  // Fluss-Counts: beobachteter Fluss (connection._total) ODER PD-Szenario (pidCounts)
  const fc = useMemo(() => {
    if (connection?._total != null) return { total: connection._total, completed: connection._completed || 0, failed: connection._failed || 0, hourly: connection._hourly };
    const pc = stats?.pidCounts?.[connection?.pid];
    return pc ? { total: pc.total || 0, completed: pc.completed || 0, failed: pc.failed || 0, hourly: pc.hourly } : { total: 0, completed: 0, failed: 0, hourly: null };
  }, [connection, stats]);

  const lastMsg = scenarioMessages[0];
  const status = lastMsg?.status || (fc.failed > 0 ? "ERROR" : fc.total > 0 ? "COMPLETED" : "UNKNOWN");
  const statusCls = status === "COMPLETED" ? "ok" : status === "FAILED" || status === "ERROR" ? "crit" : status === "PROCESSING" ? "proc" : "idle";
  const statusLabel = status === "COMPLETED" ? t("panel.status.aktiv") : status === "FAILED" ? t("panel.status.fehler") : status === "ERROR" ? t("panel.status.fehler") : status === "PROCESSING" ? t("panel.status.verarbeitung") : t("panel.status.keiner");

  const pipelineRefs = useMemo(() => {
    const refs = [];
    stringParams.forEach(p => {
      if (p.Id.toLowerCase().startsWith("interfacedetermination")) {
        const target = p.Id.replace(/^interfaceDetermination_?/i, "");
        refs.push({ label: t("panel.ref.interface", { ziel: target || "Default" }), value: p.Value, type: "interface", param: p.Id });
      }
    });
    const preEp = stringParams.find(p => p.Id === "CustomXPreEndpoint");
    if (preEp) refs.unshift({ label: "Pre Processing Endpoint", value: preEp.Value, type: "endpoint", param: preEp.Id });
    if (connection?.iflow && !refs.some(r => r.value === connection.iflow))
      refs.unshift({ label: "Primary iFlow", value: connection.iflow, type: "iflow" });
    // Beteiligte iFlows aus den korrelierten Laeufen, also ueber die Correlation-ID verkettet
    // und in Prozessreihenfolge. Vorher speiste sich die Liste aus den geladenen Nachrichten -
    // bei einem Sammellauf sind das nur die FEHLERHAFTEN Hops, weshalb ein erfolgreich
    // durchgelaufener Einstiegs-iFlow gar nicht auftauchte und nur die Deep Search ihn kannte.
    (beteiligte || []).forEach(b => {
      const wert = b.flow || b.name;
      // b.rolle ist ein Wert vom Server ("einstieg"/"ziel") und bleibt woertlich,
      // die Beschriftung kommt aus dem Katalog.
      const label = b.rolle === "einstieg" ? t("panel.ref.einstieg") : b.rolle === "ziel" ? t("panel.ref.ziel") : t("panel.ref.beteiligt");
      const vorhanden = refs.find(r => r.value === wert);
      if (vorhanden) { vorhanden.stat = b; if (b.rolle) vorhanden.label = label; return; }
      refs.push({ label, value: wert, anzeige: b.name, type: "iflow", stat: b });
    });
    if (!beteiligte) {
      const monitorFlows = [...new Set(scenarioMessages.map(m => m.flow).filter(Boolean))];
      monitorFlows.forEach(f => {
        if (!refs.some(r => r.value === f)) refs.push({ label: t("panel.ref.verwendet"), value: f, type: "iflow" });
      });
    }
    return refs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, stringParams, scenarioMessages, beteiligte, i18n.language]);

  const hourlyData = useMemo(() => {
    const backendHourly = fc.hourly;
    if (backendHourly && backendHourly.length === 24) return backendHourly;
    const buckets = Array.from({ length: 24 }, () => 0);
    scenarioMessages.forEach(m => {
      if (!m.start) return;
      const ts = typeof m.start === "string" && m.start.includes("/Date(")
        ? new Date(parseInt(m.start.match(/\d+/)?.[0] || 0))
        : new Date(m.start);
      const hoursAgo = Math.floor((Date.now() - ts.getTime()) / 3600000);
      if (hoursAgo >= 0 && hoursAgo < 24) buckets[23 - hoursAgo]++;
    });
    return buckets;
  }, [scenarioMessages, stats, connection]);
  const maxHourly = Math.max(...hourlyData, 1);
  // Fehler je Stunde aus DERSELBEN Quelle wie die Balkenhoehe. Vorher stammte die
  // Fehlermarkierung aus der gekappten Prozessliste und widersprach dem Balken.
  const hourlyFailedData = useMemo(() => {
    const hf = connection?._hourlyFailed;
    if (Array.isArray(hf) && hf.length === 24) return hf;
    const buckets = Array.from({ length: 24 }, () => 0);
    (runs || []).forEach(r => {
      if (r.status !== "fehler") return;
      // Zeitstempel bewusst nicht "t": das wuerde die Uebersetzungsfunktion verdecken.
      const zeit = r.lastActivity || r.firstStart;
      if (!zeit) return;
      const ago = Math.floor((Date.now() - zeit) / 3600000);
      if (ago >= 0 && ago < 24) buckets[23 - ago]++;
    });
    return buckets;
  }, [connection, runs]);

  const openSuiteForFlow = (flowName) => {
    if (!tenantUrl || !instanceId) return;
    fetch(`/api/${instanceId}/resolve-package/${encodeURIComponent(flowName)}`)
      .then(r => r.json())
      .then(d => {
        if (d.packageId && d.flowId) {
          window.open(`${tenantUrl}/shell/design/contentpackage/${d.packageId}/integrationflows/${d.flowId}`, "_blank");
        } else {
          window.open(`${tenantUrl}/shell/design`, "_blank");
        }
      })
      .catch(() => window.open(`${tenantUrl}/shell/design`, "_blank"));
  };

  const renderOverview = () => (
    <div>
      <div className="dpid">{pid}</div>
      <div className="dsec">
        <div className="lbl">{t("panel.uebersicht.route")}</div>
        <div className="route">
          <div className="tn src"><span className="n">{senderName}</span><span className="s">{t("panel.uebersicht.sender")}</span></div>
          <div className={`rflow ${statusCls === "idle" ? "" : statusCls}`}><i /><i /><i /></div>
          <div className="tn dst"><span className="n">{receiverName}</span><span className="s">{t("panel.uebersicht.receiver")}</span></div>
        </div>
        {connection?.senderInterface && <div style={{ fontSize: 12, color: "var(--iio-text-3)", marginTop: 6 }}>{t("panel.uebersicht.interface", { name: connection.senderInterface })}</div>}
        {connection?.senderRuntimeAlias && connection.senderRuntimeAlias !== senderName && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11.5, color: "var(--iio-text-2)", marginTop: 8, borderLeft: "3px solid var(--iio-signal)", paddingLeft: 10 }}>
            <MuiTooltip arrow placement="top"
              slotProps={{
                popper: { className: "iio-pip-modal" },
                tooltip: { sx: { fontSize: 12, lineHeight: 1.55, maxWidth: 360, background: "var(--iio-bg-3, #163640)", color: "var(--iio-text-1, #E8EEF1)", border: "1px solid var(--iio-border, #1F4954)", p: 1.25 } },
                arrow: { sx: { color: "var(--iio-bg-3, #163640)" } },
              }}
              title={t("panel.uebersicht.aliasTitel", {
                alias: connection.senderRuntimeAlias,
                system: senderName,
                umgebung: connection.senderEnv ? t("spalten.umgebung", { env: connection.senderEnv }) : "",
              })}>
              <span tabIndex={0} role="img" aria-label={t("panel.uebersicht.aliasAria")} style={{ display: "inline-flex", cursor: "help", color: "var(--iio-signal)", flexShrink: 0, marginTop: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
              </span>
            </MuiTooltip>
            <span>{t("panel.uebersicht.aliasVor")}<b style={{ color: "var(--iio-text-1)" }}>{senderName}</b>{t("panel.uebersicht.aliasMitte")}<b>{connection.senderRuntimeAlias}</b>{connection.senderEnv ? ` (${connection.senderEnv})` : ""}{t("panel.uebersicht.aliasNach")}</span>
          </div>
        )}
      </div>
      <div className="dsec">
        <div className="lbl">{t("panel.uebersicht.pdReferenz")}</div>
        <div style={{ fontSize: 14, color: "var(--iio-text-1)" }}>{pid}</div>
      </div>
      <div className="dsec">
        <div className="lbl">{t("panel.uebersicht.status")}</div>
        <span className={`stt ${statusCls}`}>
          {statusLabel}
          {fc.total > 0 && <span className="mut">{t("panel.uebersicht.msgKurz", { zahl: zahl(fc.total), zeitraum: scopeLabel })}</span>}
        </span>
      </div>
      {connection?.configured === false && (
        <div className="dsec">
          <div className="lbl">{t("panel.uebersicht.konfiguration")}</div>
          <div style={{ fontSize: 12, color: "var(--iio-warning)", lineHeight: 1.5 }}>
            {t("panel.uebersicht.nichtKonfiguriert")}
          </div>
        </div>
      )}
      <div className="dsec">
        <div className="lbl">{t("panel.uebersicht.durchsatz")}</div>
        <div style={{ fontSize: 14, color: "var(--iio-text-1)" }}>
          {t("panel.uebersicht.durchlaeufe", { zahl: zahl(fc.total), zeitraum: scopeLabel })}
          {fc.total > 0 && <span style={{ color: "var(--iio-text-3)", fontSize: 12 }}>{t("panel.uebersicht.davon", { ok: zahl(fc.completed) })}{fc.failed > 0 ? t("panel.uebersicht.davonFehler", { fehler: zahl(fc.failed) }) : ""}</span>}
        </div>
      </div>
      <button className="dbtn" disabled={!tenantUrl}
        onClick={() => {
          if (!tenantUrl) return;
          const flows = [...new Set(scenarioMessages.map(m => m.flow).filter(Boolean))];
          const filterParts = flows.map(f => `IntegrationFlowName eq '${f}'`).join(" or ");
          const url = flows.length > 0
            ? `${tenantUrl}/shell/monitoring/Messages?filter=${encodeURIComponent(filterParts)}`
            : `${tenantUrl}/shell/monitoring/Messages`;
          window.open(url, "_blank");
        }}>
        <OpenInNewIcon sx={{ fontSize: 14 }} />{t("panel.uebersicht.messageMonitor")}
      </button>
      <button className="dbtn"
        onClick={() => emitEvent("cpi-nav-pd", { instId: instanceId, pid: connection?.pid })}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
        {t("panel.uebersicht.pdKnopf", { pid })}
      </button>
    </div>
  );

  const renderPipelineSteps = () => (
    <div style={{ paddingTop: 20 }}>
      <div className="lbl" style={{ marginBottom: 14 }}>{t("panel.schritte.titel")}</div>
      {pipelineRefs.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--iio-text-3)", fontStyle: "italic" }}>{t("panel.schritte.leer")}</div>
      ) : (
        <div className="steps">
          {pipelineRefs.map((ref, i) => (
            <div key={ref.param || ref.value} className="step">
              <div className="mk">
                <span className={`sq${ref.type === "iflow" ? " ifl" : ref.type === "endpoint" ? " ep" : ""}`} />
                {i < pipelineRefs.length - 1 && <span className="ln" />}
              </div>
              <div className="sc">
                <div className="st">{ref.label}</div>
                <div className="sv">{ref.anzeige || ref.value}</div>
                {ref.param && <div className="sp">{t("panel.schritte.pdParam", { param: ref.param })}</div>}
                {ref.stat && (
                  <div className="sp">
                    {t("panel.schritte.laeufe", { count: ref.stat.gesamt, zahl: zahl(ref.stat.gesamt) })}
                    {" "}
                    {t("panel.schritte.vorgaenge", { count: ref.stat.laeufe, zahl: zahl(ref.stat.laeufe) })}
                    {ref.stat.fehler ? t("panel.schritte.fehlerhaft", { zahl: zahl(ref.stat.fehler) }) : ""}
                  </div>
                )}
                {ref.type === "iflow" && (
                  <div className="sb">
                    <button className="tbtn acc"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onNavigate && selectedSpace && selectedInst) {
                          onNavigate("dashboard", selectedSpace, selectedInst, { flowName: ref.value });
                        }
                      }}>Webapp</button>
                    <button className="tbtn"
                      onClick={(e) => { e.stopPropagation(); openSuiteForFlow(ref.value); }}>Suite</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {stringParams.some(p => p.Id.toLowerCase().includes("namespace") || p.Id.toLowerCase().includes("serviceinterface")) && (
        <div style={{ marginTop: 20 }}>
          <div className="lbl" style={{ marginBottom: 8 }}>{t("panel.schritte.serviceKonfig")}</div>
          {stringParams.filter(p => p.Id.toLowerCase().includes("namespace") || p.Id.toLowerCase().includes("serviceinterface") || p.Id.toLowerCase().includes("receiverservice")).map((p, i) => (
            <div key={p.Id} className={`kv${i % 2 === 0 ? " odd" : ""}`}>
              <div className="kk"><span>{p.Id}</span></div>
              <div className="vv">{p.Value}</div>
            </div>
          ))}
        </div>
      )}
      {/* Deep Search - 30 Tage Lookback */}
      <div className="dsbox">
        <div className="dsh">
          <div>
            <div className="dst1">{t("panel.schritte.deepTitel")}</div>
            <div className="dst2">{t("panel.schritte.deepSub")}</div>
          </div>
          <button className="mbtn" disabled={deepSearchLoading || !connection?.receiverSystemName || !instanceId}
            onClick={async () => {
              setDeepSearchLoading(true);
              try {
                const data = await api(`/api/${instanceId}/pipeline-topology/deep-search?receiver=${encodeURIComponent(connection.receiverSystemName)}&days=30`);
                setDeepSearchResults(data);
              } catch (err) { setDeepSearchResults({ error: err.message }); }
              setDeepSearchLoading(false);
            }}>
            {deepSearchLoading ? <MuiCircularProgress size={12} sx={{ color: "var(--iio-signal)" }} /> : deepSearchResults ? t("panel.schritte.erneutSuchen") : t("gemeinsam:aktion.suchen")}
          </button>
        </div>
        {deepSearchResults && !deepSearchResults.error && (
          <div>
            <div style={{ fontSize: 11, color: "var(--iio-text-3)", margin: "10px 0 4px" }}>{t("panel.schritte.deepTreffer", { count: deepSearchResults.days, nachrichten: deepSearchResults.totalMessages })}</div>
            {deepSearchResults.flows.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--iio-text-3)", fontStyle: "italic" }}>{t("panel.schritte.keineFlows")}</div>
            ) : (
              deepSearchResults.flows.map(f => (
                <div key={f.name} className="dsrow">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dn">{f.name}</div>
                    <div className="dm">
                      {t("panel.schritte.zuletzt", {
                        anzahl: f.count,
                        datum: f.lastSeen ? new Date(parseInt(f.lastSeen.match(/\d+/)?.[0] || 0)).toLocaleDateString(gebiet) : "-",
                      })}
                      {f.statuses.COMPLETED && <span className="gok"> {f.statuses.COMPLETED} OK</span>}
                      {(f.statuses.FAILED || f.statuses.ERROR) && <span className="gerr"> {t("panel.schritte.fehlerZahl", { zahl: (f.statuses.FAILED || 0) + (f.statuses.ERROR || 0) })}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button className="tbtn acc"
                      onClick={() => { if (onNavigate && selectedSpace && selectedInst) onNavigate("dashboard", selectedSpace, selectedInst, { flowName: f.name }); }}>Webapp</button>
                    <button className="tbtn" onClick={() => openSuiteForFlow(f.name)}>Suite</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {deepSearchResults?.error && (
          <div style={{ fontSize: 12, color: "var(--iio-critical)", marginTop: 8 }}>{deepSearchResults.error}</div>
        )}
      </div>
    </div>
  );

  const hasPdEdits = Object.keys(pdEdits).length > 0;
  const pdChangeSummary = Object.entries(pdEdits).map(([paramId, newVal]) => {
    const orig = stringParams.find(p => p.Id === paramId);
    return { paramId, oldValue: orig?.Value || "", newValue: newVal };
  }).filter(c => c.oldValue !== c.newValue);

  const handlePdSave = async () => {
    setPdSaving(true);
    setPdSaveResult(null);
    try {
      for (const change of pdChangeSummary) {
        await api(`/api/${instanceId}/partner-directory/string-params/${encodeURIComponent(pid)}/${encodeURIComponent(change.paramId)}`, {
          method: "PUT", body: JSON.stringify({ Value: change.newValue }),
        });
      }
      setPdSaveResult({ ok: true, count: pdChangeSummary.length });
      setPdEdits({});
      setPdConfirmOpen(false);
    } catch (e) {
      setPdSaveResult({ ok: false, error: e.message });
    }
    setPdSaving(false);
  };

  const renderPartnerDirectory = () => (
    <div style={{ paddingTop: 20 }}>
      <div className="dsec">
        <div className="lbl">PID</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--iio-text-1)" }}>{pid}</div>
        {selectedInst && <div style={{ fontSize: 11, color: "var(--iio-text-3)", marginTop: 4 }}>{t("panel.pd.tenant", { name: selectedInst.name || selectedInst.id })}</div>}
      </div>
      {pdSaveResult && (
        <div className={`note ${pdSaveResult.ok ? "ok" : "bad"}`}>
          {pdSaveResult.ok ? t("panel.pd.gespeichert", { count: pdSaveResult.count }) : t("panel.pd.speicherFehler", { meldung: pdSaveResult.error })}
        </div>
      )}
      <div className="lbl" style={{ marginBottom: 8 }}>{t("panel.pd.stringParams", { anzahl: stringParams.length })}</div>
      {stringParams.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          {stringParams.map((p, i) => {
            const isEditing = Object.prototype.hasOwnProperty.call(pdEdits, p.Id);
            const currentVal = isEditing ? pdEdits[p.Id] : p.Value;
            return (
              <div key={p.Id} className={`kv${i % 2 === 0 ? " odd" : ""}`}>
                <div className="kk">
                  <span>{p.Id}</span>
                  {!isEditing ? (
                    <button className="tbtn" onClick={() => setPdEdits(prev => ({ ...prev, [p.Id]: p.Value }))}>{t("gemeinsam:aktion.bearbeiten")}</button>
                  ) : (
                    <button className="tbtn" onClick={() => setPdEdits(prev => { const n = { ...prev }; delete n[p.Id]; return n; })}>{t("gemeinsam:aktion.abbrechen")}</button>
                  )}
                </div>
                {isEditing ? (
                  <MuiTextField
                    fullWidth size="small" multiline maxRows={4}
                    value={currentVal}
                    onChange={(e) => setPdEdits(prev => ({ ...prev, [p.Id]: e.target.value }))}
                    sx={{
                      "& .MuiInputBase-root": { fontFamily: FONT, fontSize: 13, color: "var(--iio-text-1)", background: "var(--iio-bg-1)" },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-signal) !important" },
                    }}
                  />
                ) : (
                  <div className="vv">{p.Value}</div>
                )}
              </div>
            );
          })}
        </div>
      ) : <div style={{ fontSize: 13, color: "var(--iio-text-3)", fontStyle: "italic", marginBottom: 24 }}>{t("panel.pd.keineStringParams")}</div>}

      {pdChangeSummary.length > 0 && (
        <button className="savebar" onClick={() => { setPdSaveResult(null); setPdConfirmOpen(true); }}>
          {t("panel.pd.aenderungenSpeichern", { count: pdChangeSummary.length })}
        </button>
      )}

      <div className="lbl" style={{ marginBottom: 8 }}>{t("panel.pd.altPartner", { anzahl: altPartners.length })}</div>
      {altPartners.length > 0 ? (
        <div>
          {altPartners.map((p, i) => (
            <div key={`${p.Scheme}_${p.Agency}_${i}`} className={`kv${i % 2 === 0 ? " odd" : ""}`}>
              <div className="kk"><span style={{ color: "var(--iio-signal)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>{p.Scheme}</span></div>
              <div className="vv">Agency: {p.Agency || "-"}</div>
              <div className="vv" style={{ color: "var(--iio-text-2)" }}>Id: {p.Id || "-"}</div>
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 13, color: "var(--iio-text-3)", fontStyle: "italic" }}>{t("panel.pd.keineAltPartner")}</div>}

      <MuiDialog open={pdConfirmOpen} onClose={() => setPdConfirmOpen(false)}
        PaperProps={{ className: "iio-pip-modal", sx: { background: "var(--iio-bg-1)", color: "var(--iio-text-1)", fontFamily: FONT, minWidth: 380, border: "1px solid var(--iio-border)" } }}>
        <MuiDialogTitle sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700 }}>{t("panel.pd.dialogTitel")}</MuiDialogTitle>
        <MuiDialogContent>
          <div className="ms">
            {t("panel.pd.dialogVor")}<b>{selectedInst?.name || t("panel.unbekannt")}</b>{t("panel.pd.dialogNach")}
          </div>
          {pdChangeSummary.map(c => (
            <div key={c.paramId} className="diff">
              <div className="dk">{c.paramId}</div>
              <div className="do">- {c.oldValue}</div>
              <div className="dn2">+ {c.newValue}</div>
            </div>
          ))}
          <div className="mwarn">
            {t("panel.pd.dialogWarnung", { pid })}
          </div>
        </MuiDialogContent>
        <MuiDialogActions sx={{ padding: "12px 24px" }}>
          <MuiButton onClick={() => setPdConfirmOpen(false)} disabled={pdSaving}
            sx={{ fontFamily: FONT, textTransform: "none", color: "var(--iio-text-3)" }}>{t("gemeinsam:aktion.abbrechen")}</MuiButton>
          <MuiButton onClick={handlePdSave} disabled={pdSaving} variant="contained"
            sx={{ fontFamily: FONT, textTransform: "none", background: "var(--iio-signal)", boxShadow: "none", "&:hover": { background: "var(--iio-signal)", filter: "brightness(1.1)", boxShadow: "none" } }}>
            {pdSaving ? t("panel.pd.speichert") : t("panel.pd.bestaetigenSpeichern")}
          </MuiButton>
        </MuiDialogActions>
      </MuiDialog>
    </div>
  );

  const loadMonError = async (guid) => {
    if (monErrorDetail?.guid === guid) { setMonErrorDetail(null); return; }
    setMonErrorDetail({ guid, loading: true });
    try {
      const [h, e, att] = await Promise.all([
        api(`/api/${instanceId}/logs/${encodeURIComponent(guid)}/headers`).catch(() => []),
        api(`/api/${instanceId}/logs/${encodeURIComponent(guid)}/error`).catch(() => null),
        api(`/api/${instanceId}/logs/${encodeURIComponent(guid)}/attachments`).catch(() => []),
      ]);
      setMonErrorDetail({ guid, headers: h, error: e, attachments: att || [], loading: false });
    } catch { setMonErrorDetail({ guid, headers: [], error: null, attachments: [], loading: false }); }
  };

  const runMonAi = async (guid, flowName, errorMsg, hdrs) => {
    if (!selectedInst?.aiCapabilities?.enabled) return;
    if (monAi[guid]) { setMonAi(p => { const n = { ...p }; delete n[guid]; return n; }); return; }
    setMonAiLoading(guid);
    try {
      const r = await api(`/api/${instanceId}/ai-error-analysis`, {
        method: "POST", body: JSON.stringify({ errorMessage: errorMsg, flowName, messageGuid: guid, customHeaders: hdrs || [], sprache: i18n.language === "en" ? "en" : "de" }),
      });
      setMonAi(p => ({ ...p, [guid]: r.analysis }));
    } catch (e) { setMonAi(p => ({ ...p, [guid]: t("panel.mon.fehlerMeldung", { meldung: e.message }) })); }
    setMonAiLoading(null);
  };

  const [monRetryLoading, setMonRetryLoading] = useState(null);
  const [monRetryResult, setMonRetryResult] = useState({});
  const [monAttachContent, setMonAttachContent] = useState({});
  const [monExpandedSection, setMonExpandedSection] = useState({});
  const [selectedHourBucket, setSelectedHourBucket] = useState(null);
  const [openStation, setOpenStation] = useState(null);   // "<cid>:<Stationsname>"
  const [nurFehler, setNurFehler] = useState(startNurFehler);
  const [bucketRuns, setBucketRuns] = useState(null);      // Prozesse der gewaehlten Stunde
  const [bucketLoading, setBucketLoading] = useState(false);

  const triggerMonRetry = async (msg) => {
    if (monRetryLoading === msg.id) return;
    setMonRetryLoading(msg.id);
    try {
      const r = await api(`/api/${instanceId}/pipeline/retry`, {
        method: "POST", body: JSON.stringify({ messageGuid: msg.id, correlationId: msg.correlationId || "", queueName: msg.receiver || "" }),
      });
      setMonRetryResult(p => ({ ...p, [msg.id]: r }));
    } catch (e) { setMonRetryResult(p => ({ ...p, [msg.id]: { success: false, message: e.message } })); }
    setMonRetryLoading(null);
  };

  const loadAttachmentContent = async (attachId) => {
    if (monAttachContent[attachId]) return;
    try {
      const guid = monErrorDetail?.guid;
      const resp = await fetch(`/api/${instanceId}/logs/${encodeURIComponent(guid)}/attachments/${encodeURIComponent(attachId)}`, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      const text = await resp.text();
      setMonAttachContent(p => ({ ...p, [attachId]: text }));
    } catch (e) { setMonAttachContent(p => ({ ...p, [attachId]: t("panel.mon.fehlerMeldung", { meldung: e.message }) })); }
  };

  const toggleSection = (guid, section) => {
    setMonExpandedSection(p => {
      const key = `${guid}:${section}`;
      return { ...p, [key]: !p[key] };
    });
  };

  const renderMd = (text) => {
    if (!text) return null;
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const lines = text.split("\n");
    return lines.map((line, i) => {
      let content = esc(line)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/`([^`]+)`/g, "<code>$1</code>");
      if (line.startsWith("## ")) return <div key={i} style={{ fontSize: 14, fontWeight: 700, color: "var(--iio-text-1)", marginTop: i > 0 ? 14 : 0, marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: content.slice(3) }} />;
      if (line.startsWith("### ")) return <div key={i} style={{ fontSize: 13, fontWeight: 600, color: "var(--iio-text-1)", marginTop: 10, marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: content.slice(4) }} />;
      if (line.startsWith("- ")) return <div key={i} style={{ paddingLeft: 12, position: "relative" }} dangerouslySetInnerHTML={{ __html: "• " + content.slice(2) }} />;
      if (line.trim() === "---") return <hr key={i} style={{ border: "none", borderTop: "1px solid var(--iio-border)", margin: "10px 0" }} />;
      if (line.trim() === "") return <div key={i} style={{ height: 6 }} />;
      return <div key={i} dangerouslySetInnerHTML={{ __html: content }} />;
    });
  };

  // Fenster eines Stundenbalkens. Der Balken i steht fuer die Stunde (23-i) vor jetzt; das
  // Fenster wird auf volle Stunden gerundet, damit es mit den Worker-Buckets deckungsgleich ist.
  const bucketFenster = (i) => {
    const jetztStunde = Math.floor(Date.now() / 3600000) * 3600000;
    const von = jetztStunde - (23 - i) * 3600000;
    return { von, bis: von + 3600000 };
  };

  // Klick auf einen Balken laedt die Prozesse GENAU dieser Stunde nach. Vorher wurde die
  // bereits geladene (auf 50 gekappte, nach letzter Aktivitaet einsortierte) Liste lokal
  // gefiltert - das lieferte regelmaessig nichts, obwohl der Balken Nachrichten zeigte,
  // weil Balken (Nachrichten aus dem Worker) und Liste (korrelierte Prozesse) zwei
  // verschiedene Quellen sind.
  useEffect(() => {
    if (selectedHourBucket === null) { setBucketRuns(null); return; }
    const sender = connection?.senderRaw, iface = connection?.senderInterface;
    if (!instanceId || connection?.idle || (!sender && !iface)) { setBucketRuns([]); return; }
    const { von, bis } = bucketFenster(selectedHourBucket);
    const qs = new URLSearchParams();
    if (sender) qs.set("sender", sender);
    if (iface) qs.set("iface", iface);
    qs.set("fromMs", String(von));
    qs.set("toMs", String(bis));
    qs.set("limit", "100");
    // Kurze Verzoegerung: wer sich durch die 24 Balken klickt, soll nicht 24 OData-Abfragen
    // gleichzeitig ausloesen. Das verworfen-Flag verhindert zusaetzlich, dass eine spaet
    // eintreffende Antwort eine neuere ueberschreibt.
    let verworfen = false;
    setBucketLoading(true);
    // Handle bewusst nicht "t": das wuerde die Uebersetzungsfunktion verdecken.
    const warte = setTimeout(() => {
      api(`/api/${instanceId}/pipeline-topology/scenario-runs?${qs.toString()}`)
        .then(d => { if (!verworfen) setBucketRuns(Array.isArray(d?.runs) ? d.runs : []); })
        .catch(() => { if (!verworfen) setBucketRuns([]); })
        .finally(() => { if (!verworfen) setBucketLoading(false); });
    }, 200);
    return () => { verworfen = true; clearTimeout(warte); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHourBucket, connection?.id, instanceId]);

  const displayedRuns = selectedHourBucket !== null ? (bucketRuns || []) : (runs || []);
  // "Nur fehlerhafte" filtert die ANZEIGE, nicht die geladene Menge - die Gesamtzahl bleibt
  // daneben stehen, damit der Filter nicht als Bestand missverstanden wird.
  const gezeigteRuns = nurFehler ? displayedRuns.filter(r => r.status === "fehler") : displayedRuns;

  const fmtBucket = (i) => {
    const { von, bis } = bucketFenster(i);
    // Tag und Monat folgen der Sprache: im Deutschen Tag.Monat, im Englischen Monat/Tag.
    const f = (ms) => {
      const d = new Date(ms);
      const tag = d.getDate().toString().padStart(2, "0");
      const monat = (d.getMonth() + 1).toString().padStart(2, "0");
      const stunde = d.getHours().toString().padStart(2, "0");
      return `${i18n.language === "en" ? `${monat}/${tag}` : `${tag}.${monat}`} ${stunde}`;
    };
    return t("panel.mon.bucketFenster", { von: f(von), bis: f(bis) });
  };

  // Einzellauf-Liste einer Station. Als Funktion, damit sie DIREKT unter der angeklickten
  // Zeile stehen kann - vorher hing sie unter der ganzen Tabelle und man musste scrollen.
  const phopsBlock = (eintraege, posFn) => (
    <div className="phops">
      {(eintraege || []).map((h, i, arr) => {
              const msg = { id: h.guid, status: h.status, start: h.start, flow: h.flow, label: h.name, receiver: h.receiver };
              const pos = posFn(h, i, arr);
              const isFailed = msg.status === "FAILED" || msg.status === "ERROR";
              const msgCls = msg.status === "COMPLETED" ? "ok" : isFailed ? "err" : msg.status === "PROCESSING" ? "proc" : "idle";
              const msgLabel = msg.status === "COMPLETED" ? "OK" : isFailed ? t("panel.mon.fehler") : msg.status;
              const ts = msg.start ? (typeof msg.start === "string" && msg.start.includes("/Date(") ? new Date(parseInt(msg.start.match(/\d+/)?.[0] || 0)) : new Date(msg.start)) : null;
              const timeStr = ts ? ts.toLocaleString(gebiet, { hour: "2-digit", minute: "2-digit", second: "2-digit", day: "2-digit", month: "2-digit" }) : "-";
              const suiteLink = tenantUrl && msg.id ? `${tenantUrl}/shell/monitoring/Messages/${msg.id}` : null;
              const isExpanded = monErrorDetail?.guid === msg.id;
              const secKey = (s) => `${msg.id}:${s}`;
              return (
                <div key={msg.id || i} className={`msg${i % 2 === 0 ? " odd" : ""}${isExpanded ? " openx" : ""}`}>
                  <div className="mrow" onClick={() => { if (msg.id) loadMonError(msg.id); }}>
                    <span className={`mled ${msgCls}${h.riss ? " riss" : ""}`} />
                    <span className={`mst ${msgCls}`}>{msgLabel}</span>
                    <span className="mfl"><span className="ppos">{pos}</span>{msg.label || msg.flow || ""}</span>
                    <span className="mtm">{timeStr}</span>
                    <MuiTooltip title={t("panel.mon.inSuite")} arrow placement="top">
                      <span className="mic"
                        onClick={(e) => { e.stopPropagation(); if (suiteLink) window.open(suiteLink, "_blank"); }}>
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </span>
                    </MuiTooltip>
                    {msg.flow && (
                      <MuiTooltip title={t("panel.mon.inWebapp", { name: msg.flow })} arrow placement="top">
                        <span className="mic"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onNavigate && selectedSpace && selectedInst) onNavigate("dashboard", selectedSpace, selectedInst, { flowName: msg.flow });
                          }}>
                          <LaunchIcon sx={{ fontSize: 14 }} />
                        </span>
                      </MuiTooltip>
                    )}
                  </div>
                  {isExpanded && !monErrorDetail.loading && (
                    <div className="mexp">
                      {/* Fehler Kontext */}
                      {monErrorDetail.error?.message && (
                        <div style={{ marginBottom: 12 }}>
                          <div className="lbl" style={{ marginBottom: 6 }}>{t("panel.mon.fehlerKontext")}</div>
                          <pre className="errctx">{monErrorDetail.error.message}</pre>
                        </div>
                      )}

                      {/* Aktionen */}
                      <div className="mbtns">
                        {isFailed && (
                          <button className="mbtn acc"
                            disabled={!selectedInst?.aiCapabilities?.enabled || monAiLoading === msg.id}
                            title={selectedInst?.aiCapabilities?.enabled ? undefined : t("panel.mon.kiAus")}
                            onClick={() => runMonAi(msg.id, msg.flow, monErrorDetail.error?.message, monErrorDetail.headers)}>
                            {monAiLoading === msg.id ? t("panel.mon.analysiert") : monAi[msg.id] ? t("panel.mon.kiErneut") : t("panel.mon.kiAnalyse")}
                          </button>
                        )}
                        {isFailed && (
                          <button className="mbtn"
                            disabled={monRetryLoading === msg.id}
                            onClick={() => triggerMonRetry(msg)}>
                            {monRetryLoading === msg.id ? "..." : monRetryResult[msg.id]?.success ? t("panel.mon.retryGesendet") : t("panel.mon.nachverarbeiten")}
                          </button>
                        )}
                        <button className={`mbtn${monExpandedSection[secKey("headers")] ? " on" : ""}`}
                          onClick={() => toggleSection(msg.id, "headers")}>
                          {t("panel.mon.headers", { anzahl: monErrorDetail.headers?.length || 0 })}
                        </button>
                        {(monErrorDetail.attachments?.length || 0) > 0 && (
                          <button className={`mbtn${monExpandedSection[secKey("attachments")] ? " on" : ""}`}
                            onClick={() => toggleSection(msg.id, "attachments")}>
                            {t("panel.mon.attachments", { anzahl: monErrorDetail.attachments.length })}
                          </button>
                        )}

                      </div>

                      {/* Retry-Ergebnis */}
                      {monRetryResult[msg.id] && (
                        <div className={`note ${monRetryResult[msg.id].success ? "ok" : "warn"}`} style={{ marginBottom: 10 }}>
                          {monRetryResult[msg.id].message || (monRetryResult[msg.id].success ? t("panel.mon.retryOk") : t("panel.mon.retryFail"))}
                        </div>
                      )}

                      {/* Headers */}
                      {monExpandedSection[secKey("headers")] && (
                        <div className="subbox">
                          <div className="lbl" style={{ marginBottom: 8 }}>{t("panel.mon.customHeaders")}</div>
                          {(monErrorDetail.headers?.length || 0) > 0 ? (
                            <div>
                              {monErrorDetail.headers.map((h, hi) => (
                                <div key={hi} className="hdrrow">
                                  <span className="hk">{h.Name}</span>
                                  <span className="hvv">{h.Value}</span>
                                </div>
                              ))}
                            </div>
                          ) : <div style={{ fontSize: 12, color: "var(--iio-text-3)", fontStyle: "italic" }}>{t("panel.mon.keineHeaders")}</div>}
                        </div>
                      )}

                      {/* Attachments */}
                      {monExpandedSection[secKey("attachments")] && monErrorDetail.attachments?.length > 0 && (
                        <div className="subbox">
                          <div className="lbl" style={{ marginBottom: 8 }}>{t("panel.mon.attachmentsTitel")}</div>
                          {monErrorDetail.attachments.map((att, ai) => (
                            <div key={ai} className="attrow">
                              <div className="ah">
                                <div>
                                  <div className="an">{att.Name}</div>
                                  <div className="am">{att.ContentType} - {att.PayloadSize ? `${Math.round(att.PayloadSize / 1024)}KB` : "-"}</div>
                                </div>
                                <button className="tbtn acc" onClick={() => loadAttachmentContent(att.Id)}>
                                  {monAttachContent[att.Id] ? t("panel.mon.geladen") : t("panel.mon.laden")}
                                </button>
                              </div>
                              {monAttachContent[att.Id] && (
                                <pre className="attpre">{monAttachContent[att.Id]}</pre>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* KI-Analyse */}
                      {monAi[msg.id] && (
                        <div className="ai">
                          {renderMd(monAi[msg.id])}
                        </div>
                      )}
                    </div>
                  )}
                  {isExpanded && monErrorDetail.loading && (
                    <div style={{ padding: 16, textAlign: "center" }}><MuiCircularProgress size={18} sx={{ color: "var(--iio-signal)" }} /></div>
                  )}
                </div>
              );
            })}
    </div>
  );

  const renderMonitoring = () => (
    <div style={{ paddingTop: 20 }}>
      <div className="dsec">
        <div className="lbl" style={{ marginBottom: 12 }}>{t("panel.mon.durchlaeufeKopf", { zeitraum: scopeLabel, anzahl: zahl(fc.total || scenarioMessages.length) })}</div>
        {(scenarioMessages.length > 0 || fc.total > 0) ? (
          <>
            <div className="hist" onClick={(e) => { if (e.target === e.currentTarget) setSelectedHourBucket(null); }}>
              {hourlyData.map((val, i) => {
                const isSelected = selectedHourBucket === i;
                const hasErrors = (hourlyFailedData[i] || 0) > 0;
                const errorCount = hourlyFailedData[i] || 0;
                return (
                  <MuiTooltip key={i} title={`${t("panel.mon.balkenTitel", { count: val, fenster: fmtBucket(i) })}${errorCount > 0 ? t("panel.mon.balkenFehler", { zahl: errorCount }) : ""}${t("panel.mon.balkenHinweis")}`} arrow placement="top">
                    <div
                      className={`hbar${val === 0 ? " zero" : ""}${hasErrors ? " err" : ""}${isSelected ? " sel" : ""}`}
                      onClick={(e) => { e.stopPropagation(); if (val === 0) return; setSelectedHourBucket(isSelected ? null : i); setMonErrorDetail(null); setMonRetryResult({}); setMonAttachContent({}); setMonExpandedSection({}); }}
                    >
                      <span className="hv">{val}</span>
                      <i style={{ height: `${Math.max((val / maxHourly) * 100, val > 0 ? 8 : 4)}%` }} />
                    </div>
                  </MuiTooltip>
                );
              })}
            </div>
            <div className="haxis">
              <span>{t("panel.mon.achseVor")}</span>
              {selectedHourBucket !== null && (
                <span className="hclr" onClick={() => setSelectedHourBucket(null)}>
                  {t("panel.mon.filterAufheben", { fenster: fmtBucket(selectedHourBucket) })}
                  {bucketLoading ? t("panel.mon.wirdGeladen") : t("panel.mon.prozesseZahl", { count: (bucketRuns || []).length })}
                </span>
              )}
              <span>{t("panel.mon.achseJetzt")}</span>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: "var(--iio-text-3)", fontStyle: "italic", padding: "12px 0" }}>
            {t("panel.mon.keineNachrichten")}
          </div>
        )}
      </div>
      <div>
        <div className="prunhead">
          <span className="lbl">
            {selectedHourBucket !== null
              ? t("panel.mon.durchlaeufeFenster", { fenster: fmtBucket(selectedHourBucket), anzahl: displayedRuns.length })
              : !(runs || []).length
                ? t("panel.mon.durchlaeufeTitel")
                : nurFehler
                  ? t("panel.mon.durchlaeufeGefiltert", { anzahl: gezeigteRuns.length, gesamt: runs.length })
                  : t("panel.mon.durchlaeufeAnzahl", { anzahl: gezeigteRuns.length })}
          </span>
          {(runs || []).some(r => r.status === "fehler") && (
            <span className="prunfil">
              <button type="button" className={nurFehler ? "" : "an"} onClick={() => setNurFehler(false)}>{t("panel.mon.alle")}</button>
              <button type="button" className={nurFehler ? "an" : ""} onClick={() => setNurFehler(true)}>
                {t("panel.mon.nurFehlerhafte", { anzahl: (runs || []).filter(r => r.status === "fehler").length })}
              </button>
            </span>
          )}
        </div>
        {runsKey && (
          <div className={`ptrace${runsKey.quote >= 90 ? " ok" : runsKey.quote > 0 ? " teil" : " off"}`}>
            {runsKey.quote >= 90
              ? t("panel.mon.spurVoll")
              : runsKey.quote > 0
                ? t("panel.mon.spurTeil", { quote: runsKey.quote })
                : t("panel.mon.spurKeine")}
          </div>
        )}
        {msgsLoading ? (
          <div style={{ padding: 16, textAlign: "center" }}><MuiCircularProgress size={18} sx={{ color: "var(--iio-signal)" }} /></div>
        ) : gezeigteRuns.length > 0 ? (
          <div>
            {gezeigteRuns.map((run, ri) => {
              const rk = run.cid || `x${ri}`;
              const runOpen = openRun === rk;
              const rcls = run.status === "fehler" ? "err" : run.status === "wiederholung" ? "warn"
                : run.status === "laeuft" ? "proc" : run.status === "ohne_ziel" ? "warn" : "ok";
              // run.status traegt Werte vom Server ("fehler", "laeuft", ...). Die bleiben
              // woertlich, uebersetzt wird nur die Beschriftung.
              const rlabel = run.status === "fehler" ? t("panel.mon.laufFehler") : run.status === "wiederholung" ? t("panel.mon.laufWiederholung")
                : run.status === "laeuft" ? t("panel.mon.laufLaeuft") : run.status === "ohne_ziel" ? t("panel.mon.laufOhneZiel") : t("panel.mon.laufErfolg");
              const rts = run.lastActivity ? new Date(run.lastActivity) : null;
              const rtime = rts ? rts.toLocaleString(gebiet, { hour: "2-digit", minute: "2-digit", second: "2-digit", day: "2-digit", month: "2-digit" }) : "-";
              const dauer = run.durationMs == null ? "" : run.durationMs < 1000 ? t("panel.mon.dauerMs", { n: run.durationMs })
                : run.durationMs < 60000 ? t("panel.mon.dauerSek", { n: (run.durationMs / 1000).toFixed(1) }) : t("panel.mon.dauerMin", { n: Math.round(run.durationMs / 60000) });
              const stationen = run.stationen || [];
              // Wiederholt heisst: dieselbe Station lief mehrfach NACHEINANDER. Das ist die
              // Aussage, die den Sammellauf-Text ersetzt, wo er nicht zutrifft.
              const wiederholt = stationen.filter(s => s.art === "wiederholung");
              return (
                <div key={rk} className={`prun${runOpen ? " open" : ""}`}>
                  <div className="prow" onClick={() => setOpenRun(runOpen ? null : rk)}>
                    <span className={`mled ${rcls}`} />
                    <span className={`mst ${rcls}`}>{rlabel}</span>
                    <span className="pfl">
                      {/* Alle bedienten Ziele nennen: ein Split-Vorgang geht in mehrere
                          Systeme gleichzeitig - nur den zuletzt aktiven Zweig zu zeigen
                          las sich wie "ging an ECC statt an ORA_EV". */}
                      {(Array.isArray(run.empfaenger) && run.empfaenger.length
                        ? (run.empfaenger.slice(0, 3).join(" + ") + (run.empfaenger.length > 3 ? ` +${run.empfaenger.length - 3}` : ""))
                        : run.receiver) || run.iface || t("panel.mon.prozess")}
                      {run.appMsgId ? <span className="pkey" title={t("panel.mon.schluesselTitel")}>{run.appMsgId}</span> : null}
                      <span className="psub">{
                        // Wiederholungen NICHT als Sammellauf ausgeben. Ein Vorgang, dessen
                        // Zustellung viermal versucht wurde, ist nicht aufgefaechert.
                        wiederholt.length
                          ? `${t("panel.mon.psubStationen", { count: stationen.length })}${t("panel.mon.psubWiederholungen", { count: wiederholt.reduce((s, x) => s + (x.gesamt - 1), 0) })}${run.fehlerAnzahl ? t("panel.mon.psubFehlerhaft", { zahl: zahl(run.fehlerAnzahl) }) : ""}`
                          : run.sammellauf
                            ? `${t("panel.mon.psubSammellauf", { count: run.gesamtSchritte || run.hopCount, zahl: zahl(run.gesamtSchritte || run.hopCount) })}${run.fehlerAnzahl ? t("panel.mon.psubFehlerhaft", { zahl: zahl(run.fehlerAnzahl) }) : ""}`
                            : t("panel.mon.psubStationen", { count: stationen.length || run.hopCount })
                      }{dauer ? ` · ${dauer}` : ""}</span>
                    </span>
                    <span className="mtm">{rtime}</span>
                    <svg className="pchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                  {run.status === "ohne_ziel" && (
                    <div className="pnote">{t("panel.mon.ohneZiel", { minuten: runsStale })}</div>
                  )}
                  {runOpen && run.sammellauf && (
                    <div className="pbundle">
                      <div className="bhead">
                        {wiederholt.length ? (<>
                          {t("panel.mon.buendelWdh1")}<b>{t("panel.mon.buendelEin")}</b>{t("panel.mon.buendelWdh2")}
                          <b>{run.einstieg}</b>{t("panel.mon.buendelWdh3", { count: wiederholt.length })}
                          <b>{t("panel.mon.buendelNacheinander")}</b>{t("panel.mon.buendelWdh4")}
                        </>) : (<>
                          {t("panel.mon.buendelAuf1")}<b>{run.einstieg}</b>{t("panel.mon.buendelAuf2")}
                          <b>{t("panel.mon.buendelEin")}</b>{t("panel.mon.buendelAuf3")}
                        </>)}
                      </div>
                      {!run.hatSchluessel && (
                        <div className="bwarn">
                          {t("panel.mon.buendelOhneSchluessel")}
                        </div>
                      )}
                      {run.hatSchluessel && (
                        <div className="btab" style={{ marginBottom: 8 }}>
                          {(run.jeSchluessel || []).map(k => (
                            <div className="brow" key={k.key}>
                              <span className="bn">{k.key}</span>
                              <span className="bc">{k.gesamt}</span>
                              <span className={`bf${k.fehler ? " has" : ""}`}>{k.fehler ? t("panel.mon.fehlerZahl", { zahl: k.fehler }) : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Stationsuebersicht: eine Zeile je iFlow in Prozessreihenfolge. Wiederholungen
                      stehen als EINE Zeile mit Zaehler, die Einzelversuche kommen erst beim
                      Aufklappen. Vorher lagen acht gleichrangige Fehlerzeilen untereinander, in
                      denen weder der erfolgreiche Einstieg noch der Wiederholungscharakter zu
                      sehen war. */}
                  {runOpen && stationen.length > 0 && (
                    <div className="pstat">
                      <div className="psthead">
                        <span />
                        <span>{t("panel.mon.spalteStation")}</span>
                        <span className="r">{t("panel.mon.spalteLaeufe")}</span>
                        <span className="r">{t("panel.mon.spalteStatus")}</span>
                        <span />
                      </div>
                      {stationen.map(st => {
                        const stKey = `${rk}:${st.name}`;
                        const stOpen = openStation === stKey;
                        const stCls = st.fehler ? "err" : st.ok ? "ok" : "idle";
                        const rolle = st.rolle === "einstieg" ? t("panel.mon.rolleEinstieg") : st.rolle === "ziel" ? t("panel.mon.rolleZiel") : "";
                        // Zweitzeile statt Marke neben der Zahl: die Wiederholung ist eine
                        // Erklaerung, kein Etikett - sie gehoert in die Sprache, nicht in ein
                        // farbiges Kaestchen.
                        const zusatz = st.art === "wiederholung"
                          ? `${t("panel.mon.wiederholungen", { count: st.gesamt - 1 })}${st.abstaendeSek.length ? t("panel.mon.abstaende", { liste: st.abstaendeSek.join(" / ") }) : ""}`
                          : st.art === "parallel" ? t("panel.mon.parallel") : "";
                        return (
                          <Fragment key={st.name}>
                            <div className={`pstrow${stOpen ? " open" : ""}`}
                              onClick={() => setOpenStation(stOpen ? null : stKey)}>
                              <span className={`mled ${stCls}`} />
                              <span className="pstn">
                                {/* Rolle UEBER den Namen: hinter dem Namen wurde sie vom
                                    Zeilenumbruch des Namens abgeschnitten ("EI..."). */}
                                {rolle && <span className="pstrole">{rolle}</span>}
                                <span className="nm">{st.name}</span>
                                {zusatz && <span className="pstsub">{zusatz}</span>}
                              </span>
                              <span className="pstc">{zahl(st.gesamt)}</span>
                              <span className={`pstf${st.fehler ? " has" : ""}`}>
                                {st.fehler ? t("panel.mon.fehlerZahl", { zahl: zahl(st.fehler) }) : "OK"}
                              </span>
                              <svg className="pchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
                            </div>
                            {/* Einzellaeufe DIREKT unter ihrer Station. Vorher standen sie unter
                                der ganzen Tabelle, was bei fuenf Stationen Scrollen bedeutete. */}
                            {stOpen && phopsBlock(
                              (st.versuche || []).map(v => ({ ...v, flow: st.flow, name: st.name })),
                              (h, i) => st.gesamt > 1 ? t("panel.mon.posVersuch", { n: i + 1 })
                                : st.rolle === "einstieg" ? t("panel.mon.posStart") : st.rolle === "ziel" ? t("panel.mon.posZiel") : t("panel.mon.posSchritt")
                            )}
                          </Fragment>
                        );
                      })}
                      <div className="pstnote">
                        {run.capped
                          ? t("panel.mon.stationenGekappt", { zahl: zahl(run.hopCount) })
                          : t("panel.mon.stationenAlle")}{t("panel.mon.stationenHinweis")}
                      </div>
                    </div>
                  )}
                  {runOpen && !stationen.length && phopsBlock(run.hops,
                    (h, i, arr) => run.sammellauf ? t("panel.mon.posTeillauf") : i === 0 ? t("panel.mon.posStart")
                      : (h.receiver && i === arr.length - 1) ? t("panel.mon.posZiel") : t("panel.mon.posSchrittN", { n: i + 1 }))}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--iio-text-3)", fontStyle: "italic" }}>
            {selectedHourBucket !== null ? t("panel.mon.keineNachrichtenStunde")
              : (connection?.configured === false && fc.total > 0)
                ? t("panel.mon.nurImMonitor", { zahl: zahl(fc.total), zeitraum: scopeLabel })
                : t("panel.mon.keineDaten")}
          </div>
        )}
      </div>
    </div>
  );

  const tabContent = [renderOverview, renderPipelineSteps, renderPartnerDirectory, renderMonitoring];

  return (
    <MuiDrawer anchor="right" open onClose={onClose}
      PaperProps={{ className: "iio-pip-drawer", sx: { width: 440, maxWidth: "90vw", background: "var(--iio-bg-1)", borderLeft: "1px solid var(--iio-border)", color: "var(--iio-text-1)" } }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className="dhead">
          <span className="dt">{t("panel.titel")}</span>
          <MuiIconButton onClick={onClose} size="small" sx={{ color: "var(--iio-text-3)", "&:hover": { color: "var(--iio-text-1)", background: "var(--iio-bg-3)" } }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </MuiIconButton>
        </div>
        <div className="dtabs">
          {tabs.map((label, i) => (
            <div key={label} className={`dtab${activeTab === i ? " on" : ""}`} onClick={() => setActiveTab(i)}>
              {label}
            </div>
          ))}
        </div>
        <div className="dbody">
          {tabContent[activeTab]()}
        </div>
      </div>
    </MuiDrawer>
  );
}

// ============================================================
// Lade-, Fehler- und Leerzustaende
// ============================================================

function LoadingState() {
  const { t } = useTranslation(["pipeline", "gemeinsam"]);
  const skelCol = (n, cls, title) => (
    <div className={cls}>
      <div className="iio-pip-colh"><span className="ct">{title}</span></div>
      <div className="iio-pip-cardlist">
        {Array.from({ length: n }).map((_, i) => (
          <div key={i} className="iio-pip-skel" style={{ animationDelay: `${i * 90}ms` }}>
            <i style={{ width: 2, height: 16 }} />
            <i style={{ width: `${50 + (i * 13) % 35}%`, height: 10 }} />
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="iio-pip-inner">
      <div className="iio-pip-cols">
        {skelCol(5, "iio-pip-col-s", t("spalten.kopfSender"))}
        {skelCol(8, "iio-pip-col-m", t("spalten.kopfSzenarien"))}
        {skelCol(6, "iio-pip-col-s", t("spalten.kopfReceiver"))}
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  const { t } = useTranslation(["pipeline", "gemeinsam"]);
  return (
    <div className="iio-pip-center">
      <span style={{ color: "var(--iio-critical)", fontWeight: 600, fontSize: 15 }}>
        {t("zustand.ladefehler")}
      </span>
      <span style={{ fontSize: 13, maxWidth: 400, textAlign: "center" }}>
        {message || t("zustand.unbekannterFehler")}
      </span>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

// Letzte Auswahl je BENUTZER merken (localStorage): wer regelmaessig nur PROD ansieht,
// soll nicht bei jedem Besuch von der DEV-Vorbelegung wegklicken muessen. Der Schluessel
// traegt die User-Id, damit sich mehrere Konten im selben Browser nicht mischen.
const PIP_LAST_KEY = (uid) => `cpi_pip_last:${uid || "anon"}`;
const liesLetzteWahl = (uid) => { try { return JSON.parse(window.localStorage.getItem(PIP_LAST_KEY(uid)) || "null"); } catch { return null; } };
const merkeLetzteWahl = (uid, spaceId, instId) => { try { window.localStorage.setItem(PIP_LAST_KEY(uid), JSON.stringify({ spaceId, instId })); } catch { /* Speicher voll/gesperrt - dann eben ohne Gedaechtnis */ } };

export default function PipelineTopology({ spaces, user, onNavigate }) {
  const { t, i18n } = useTranslation(["pipeline", "gemeinsam"]);
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  // === State ===
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [spaceInstances, setSpaceInstances] = useState([]);
  const [selectedInst, setSelectedInst] = useState(null);
  const [topology, setTopology] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scopeHours, setScopeHours] = useState(24); // Zeitfenster fuer die Szenario-Suche

  // === TanStack Query (nur aktiv wenn experimentalCaching=true) ===
  const {
    data: optimizedInstances, isOptimized: isInstancesOptimized,
    dataUpdatedAt: instancesUpdatedAt,
  } = useApiQuery(
    ["spaces", selectedSpace?.id, "instances"],
    `/api/spaces/${selectedSpace?.id}/instances`,
    { staleTime: 120000, enabled: !!selectedSpace?.id }
  );

  const {
    data: optimizedTopology, isOptimized: isTopoOptimized,
    dataUpdatedAt: topoUpdatedAt,
  } = useApiQuery(
    ["instance", selectedInst?.id, "pipeline-topology"],
    `/api/${selectedInst?.id}/pipeline-topology`,
    { staleTime: 300000, enabled: !!selectedInst?.id }
  );

  const {
    data: optimizedStats, isOptimized: isStatsOptimized,
    dataUpdatedAt: statsUpdatedAt,
  } = useApiQuery(
    ["instance", selectedInst?.id, "pipeline-topology-stats", scopeHours],
    `/api/${selectedInst?.id}/pipeline-topology/stats?hours=${scopeHours}`,
    { staleTime: 300000, enabled: !!selectedInst?.id }
  );

  // Optimized-Daten in lokalen State übernehmen wenn experimentalCaching aktiv
  const prevOptimizedSpaceRef = useRef(null);
  useEffect(() => {
    if (!isInstancesOptimized || !optimizedInstances) return;
    setSpaceInstances(optimizedInstances || []);
    // selectedInst nur setzen wenn Space sich geändert hat (nicht bei refetch)
    if (prevOptimizedSpaceRef.current !== selectedSpace?.id) {
      prevOptimizedSpaceRef.current = selectedSpace?.id;
      const gemerkt = liesLetzteWahl(user?.id);
      setSelectedInst((optimizedInstances || []).find(i => i.id === gemerkt?.instId) || optimizedInstances?.[0] || null);
    }
  }, [isInstancesOptimized, optimizedInstances, selectedSpace?.id]);

  useEffect(() => {
    if (!isTopoOptimized || optimizedTopology === undefined) return;
    setTopology(optimizedTopology);
    setLoading(false);
  }, [isTopoOptimized, optimizedTopology]);

  useEffect(() => {
    if (!isStatsOptimized || optimizedStats === undefined) return;
    setStats(optimizedStats);
  }, [isStatsOptimized, optimizedStats]);

  // Filter/Sort State
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortMode, setSortMode] = useState("alpha");
  const [columnSearch, setColumnSearch] = useState({ sender: "", scenario: "", receiver: "" });

  // Detail Panel State
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [detailStartTab, setDetailStartTab] = useState(0);     // Reiter beim Oeffnen
  const [detailNurFehler, setDetailNurFehler] = useState(false); // gleich auf Fehler filtern

  // Hover/Highlight: bewusst KEIN React-State (sonst re-rendert jeder Hover
  // hunderte Karten -> Lag bei vielen Eintraegen). Statt dessen Refs + direkter
  // DOM-Klassen-Tausch (analog zu den Verbindungslinien).
  const hoverRef = useRef(null);
  const highlightRef = useRef(null);
  const applyInteractionStatesRef = useRef(null);

  // Focus Mode State (selten -> React-State ist hier ok, steuert das Karten-Kollabieren)
  const [focusMode, setFocusMode] = useState(false);
  // "Nicht genutzte Pipelines": blendet konfigurierte, aber in 24h inaktive PD-Szenarien ein
  const [showUnused, setShowUnused] = useState(false);
  const [focusedCard, setFocusedCard] = useState(null);
  const focusModeRef = useRef(focusMode);
  focusModeRef.current = focusMode;

  // Canvas refs
  const canvasRef = useRef(null);
  const innerRef = useRef(null);
  const svgRef = useRef(null);

  // === Auto-select: gemerkter Space des Benutzers, sonst der erste ===
  useEffect(() => {
    if (spaces?.length && !selectedSpace) {
      const gemerkt = liesLetzteWahl(user?.id);
      setSelectedSpace(spaces.find(s => s.id === gemerkt?.spaceId) || spaces[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces]);

  // Jede wirksame Auswahl merken - auch die Vorbelegung: gespeichert ist immer genau
  // das, was der Benutzer zuletzt auf dem Schirm hatte.
  useEffect(() => {
    if (!selectedSpace || !selectedInst) return;
    merkeLetzteWahl(user?.id, selectedSpace.id, selectedInst.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpace?.id, selectedInst?.id]);

  // === Load instances when space changes (Legacy - nur wenn NICHT optimiert) ===
  useEffect(() => {
    if (isInstancesOptimized) return;
    if (!selectedSpace) { setSpaceInstances([]); setSelectedInst(null); return; }
    const load = async () => {
      try {
        const insts = await api(`/api/spaces/${selectedSpace.id}/instances`);
        setSpaceInstances(insts || []);
        const gemerkt = liesLetzteWahl(user?.id);
        setSelectedInst((insts || []).find(i => i.id === gemerkt?.instId) || insts?.[0] || null);
      } catch { setSpaceInstances([]); setSelectedInst(null); }
    };
    load();
  }, [selectedSpace, isInstancesOptimized]);

  // === Load topology when instance changes (Legacy - nur wenn NICHT optimiert) ===
  // Topologie und Stats laden getrennt: Die Spalten erscheinen, sobald die
  // Topologie da ist - die Statistiken strömen nach, sobald sie eintreffen.
  useEffect(() => {
    if (isTopoOptimized) return;
    if (!selectedInst) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api(`/api/${selectedInst.id}/pipeline-topology`)
      .then(topo => { if (!cancelled) { setTopology(topo); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [selectedInst, isTopoOptimized]);

  // Stats separat laden (auch bei Zeitraum-Wechsel, ohne die Topologie neu zu holen)
  useEffect(() => {
    if (isStatsOptimized) return;
    if (!selectedInst) { setStats(null); return; }
    let cancelled = false;
    setStats(null);
    api(`/api/${selectedInst.id}/pipeline-topology/stats?hours=${scopeHours}`)
      .then(st => { if (!cancelled) setStats(st); })
      .catch(() => { if (!cancelled) setStats(null); });
    return () => { cancelled = true; };
  }, [selectedInst, scopeHours, isStatsOptimized]);

  // === Derived data: filtered senders/receivers/connections ===
  const { senderSystems, scenarios, receiverSystems } = useMemo(() => {
    const scTerm = columnSearch.scenario.toLowerCase().trim();
    const ssTerm = columnSearch.sender.toLowerCase().trim();
    const rsTerm = columnSearch.receiver.toLowerCase().trim();
    const match = (name, term) => !term || (name || "").toLowerCase().includes(term);

    // === Beobachtungs-basiertes Modell (garantiert alle Pipeline-Nachrichten) ===
    const flows = stats?.observedFlows;
    if (Array.isArray(flows) && flows.length) {
      // Szenario-Name: der PID ist der Name, unter dem der Betreiber den Eintrag im Partner
      // Directory pflegt. Vorher stand hier der beobachtete ApplicationMessageType - deshalb
      // wechselte der Name, sobald das Szenario zum ersten Mal lief (haeufig auf den
      // Receiver-Namen). Bei einer nur ueber den Receiver GERATENEN Zuordnung bleibt es beim
      // beobachteten Namen, sonst behauptet die Oberflaeche eine Zuordnung, die nicht belegt ist.
      const pdName = (f) => (f.pid && f.pidGuete !== "schwach") ? f.pid : f.interface;
      // Fehlt eine Angabe, liefert das Backend einen festen deutschen Platzhalter
      // (server.js kennt sie als PLACEHOLDER-Menge). Das sind WERTE: sie stecken in
      // den Kennungen ss_/rs_ und in der Gruppierung, deshalb bleiben sie woertlich
      // stehen. Nur die Anzeige laeuft ueber den Katalog - sonst steht in der
      // englischen Oberflaeche "(unbekannt)" auf der Karte.
      const PLATZHALTER = {
        [WERT_OHNE_SENDER]: "spalten.ohneSender",
        [WERT_OHNE_RECEIVER]: "spalten.ohneReceiver",
        "(kein Interface)": "spalten.ohneInterface",
      };
      const anzeige = (wert) => (PLATZHALTER[wert] ? t(PLATZHALTER[wert]) : wert);
      let scs = flows.map(f => ({
        id: f.id, pid: f.pid, pidGuete: f.pidGuete || null,
        name: pdName(f), label: anzeige(pdName(f)),
        // Gruppierung nach KANONISCHEM Sender (Backend hat Alias -> Business-System-Pid aufgeloest).
        senderSystemId: "ss_" + f.senderSystem,
        senderSystemName: f.senderSystem,
        senderSystemLabel: anzeige(f.senderSystem),
        senderRuntimeAlias: f.senderRuntimeAlias || null, // roher Runtime-Alias (z.B. S_OER_PRA_100_P), wenn zusammengefasst
        senderEnv: f.senderEnv || null,                   // Umgebung des Alias (PRD/DEV/TST)
        senderRaw: f.senderRaw || f.senderSystem,
        senderInterface: f.interface, senderCertain: true,
        receiverSystemId: "rs_" + f.receiver,
        receiverSystemName: f.receiver,
        receiverSystemLabel: anzeige(f.receiver),
        configured: f.configured,
        _total: f.total, _failed: f.failed, _completed: f.completed, _hourly: f.hourly,
        _hourlyFailed: f.hourlyFailed || null,
        sampleGuid: f.sampleGuid,
      }));
      // "Nicht genutzte Pipelines": im PD konfigurierte Szenarien ohne Nachrichten in 24h
      if (showUnused && topology?.scenarios?.length) {
        const activePids = new Set(flows.filter(f => f.pid).map(f => f.pid));
        const idle = topology.scenarios.filter(sc => sc.pid && !activePids.has(sc.pid)).map(sc => ({
          id: "idle_" + sc.pid, pid: sc.pid,
          // Gleiche Benennung wie bei den beobachteten Fluessen: der PD-Eintrag ist der Name.
          name: sc.name || sc.senderInterface, label: sc.label || sc.senderInterface,
          senderSystemId: "ss_" + (sc.senderSystemName || WERT_OHNE_SENDER),
          senderSystemName: sc.senderSystemName || WERT_OHNE_SENDER,
          senderSystemLabel: sc.senderSystemName || t("spalten.ohneSender"),
          senderInterface: sc.senderInterface, senderCertain: sc.senderCertain !== false,
          receiverSystemId: "rs_" + (sc.receiverSystemName || WERT_OHNE_RECEIVER),
          receiverSystemName: sc.receiverSystemName || WERT_OHNE_RECEIVER,
          receiverSystemLabel: sc.receiverSystemName || t("spalten.ohneReceiver"),
          configured: true, idle: true,
          _total: 0, _failed: 0, _completed: 0, _hourly: Array(24).fill(0), _hourlyFailed: Array(24).fill(0),
          stringParams: sc.stringParams, altPartners: sc.altPartners, iflow: sc.iflow,
        }));
        scs = scs.concat(idle);
      }
      if (activeFilter === "error") scs = scs.filter(s => s._failed > 0);
      else if (activeFilter === "inactive") scs = scs.filter(s => !s._total);
      else if (activeFilter === "unconfigured") scs = scs.filter(s => !s.configured);
      // Szenario-Suche matcht auch den Runtime-Alias (z.B. S_OER_PRA_100_P), nicht nur den kanonischen Namen.
      // senderInterface MUSS mitsuchen: seit der Name der PID ist, faende eine Suche nach dem
      // gewohnten Interface-Namen sonst nichts mehr.
      if (scTerm) scs = scs.filter(s => match(s.name, scTerm) || match(s.senderInterface, scTerm) || match(s.senderSystemName, scTerm) || match(s.senderRuntimeAlias, scTerm) || match(s.senderRaw, scTerm) || match(s.receiverSystemName, scTerm));
      const ssMap = {}, rsMap = {};
      scs.forEach(s => {
        // name = Wert (Suche, Sortierung, Kennung), label = Anzeige.
        if (!ssMap[s.senderSystemId]) ssMap[s.senderSystemId] = { id: s.senderSystemId, name: s.senderSystemName, label: s.senderSystemLabel || s.senderSystemName, scenarioCount: 0, aliasSet: new Set() };
        ssMap[s.senderSystemId].scenarioCount++;
        if (s.senderRuntimeAlias && s.senderRuntimeAlias !== s.senderSystemName) ssMap[s.senderSystemId].aliasSet.add(s.senderRuntimeAlias);
        if (!rsMap[s.receiverSystemId]) rsMap[s.receiverSystemId] = { id: s.receiverSystemId, name: s.receiverSystemName, label: s.receiverSystemLabel || s.receiverSystemName, scenarioCount: 0 };
        rsMap[s.receiverSystemId].scenarioCount++;
      });
      let ss = Object.values(ssMap).map(s => { const { aliasSet, ...rest } = s; return { ...rest, aliases: [...aliasSet] }; }), rs = Object.values(rsMap);
      // Sender-Suche matcht auch die zusammengefassten Aliase, damit z.B. "S_OER_PRA_100_P" die OER_BS_OER-Karte findet.
      if (ssTerm) ss = ss.filter(s => match(s.name, ssTerm) || (s.aliases || []).some(a => match(a, ssTerm)));
      if (rsTerm) rs = rs.filter(r => match(r.name, rsTerm));
      const ssIds = new Set(ss.map(s => s.id)), rsIds = new Set(rs.map(r => r.id));
      scs = scs.filter(s => ssIds.has(s.senderSystemId) && rsIds.has(s.receiverSystemId));
      const sortFn = (a, b) => sortMode === "connections" ? (b.scenarioCount || 0) - (a.scenarioCount || 0) : (a.name || "").localeCompare(b.name || "", gebiet);
      ss.sort(sortFn); rs.sort(sortFn);
      scs.sort((a, b) => (b._total || 0) - (a._total || 0));
      return { senderSystems: ss, scenarios: scs, receiverSystems: rs };
    }

    // === Fallback: PD-Topology-basiert ===
    if (!topology) return { senderSystems: [], scenarios: [], receiverSystems: [] };
    const allSS = topology.senderSystems || [];
    const allSC = topology.scenarios || [];
    const allRS = topology.receiverSystems || [];
    let filteredSC = allSC.filter(sc => match(sc.name, scTerm) || match(sc.label, scTerm));
    if (activeFilter === "error") {
      const pidCounts = stats?.pidCounts || {};
      filteredSC = filteredSC.filter(sc => { const pc = pidCounts[sc.pid]; return pc && pc.failed > 0; });
    } else if (activeFilter === "inactive") {
      const pidCounts = stats?.pidCounts || {};
      filteredSC = filteredSC.filter(sc => { const pc = pidCounts[sc.pid]; return !pc || pc.total === 0; });
    }
    const usedSS = new Set(filteredSC.map(sc => sc.senderSystemId).filter(Boolean));
    const usedRS = new Set(filteredSC.map(sc => sc.receiverSystemId).filter(Boolean));
    const sortFn = (a, b) => {
      if (sortMode === "alpha") return (a.name || "").localeCompare(b.name || "", gebiet);
      if (sortMode === "connections") return (b.scenarioCount || 0) - (a.scenarioCount || 0);
      return 0;
    };
    let filteredSS = allSS.filter(s => usedSS.has(s.id)).sort(sortFn);
    if (ssTerm) filteredSS = filteredSS.filter(s => match(s.name, ssTerm) || match(s.label, ssTerm));
    let filteredRS = allRS.filter(r => usedRS.has(r.id)).sort(sortFn);
    if (rsTerm) filteredRS = filteredRS.filter(r => match(r.name, rsTerm) || match(r.label, rsTerm));
    filteredSC.sort((a, b) => (a.name || "").localeCompare(b.name || "", gebiet));
    return { senderSystems: filteredSS, scenarios: filteredSC, receiverSystems: filteredRS };
  }, [topology, columnSearch, sortMode, activeFilter, stats, showUnused, t, gebiet]);

  // Focus Mode: Esc-Taste setzt Focus zurück
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setFocusedCard(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Focus Mode: beim Deaktivieren Focus aufheben
  useEffect(() => {
    if (!focusMode) setFocusedCard(null);
  }, [focusMode]);

  // Ref für den Connection-Click-Handler (vermeidet zirkuläre Dependency)
  const handleConnectionClickRef = useRef(null);

  // Ab dieser Szenarien-Zahl werden die Ruhelinien NICHT mehr dauerhaft
  // animiert (sonst hunderte Pfade -> Dauer-Repaint -> Scroll-Lag). Die
  // wandernde Stroemung laeuft dann nur noch auf dem aktiven Pfad (.hot).
  const FLOW_ANIM_MAX = 30;

  const buildWiresRef = useRef(null);

  // === SVG-Verbindungen: Geometrie EINMAL bauen (unabhaengig vom Hover) ===
  // Richtung kommt aus der wandernden Strichelung (CSS iio-pip-flow), nicht
  // aus Pfeilspitzen. Hover/Highlight wird separat per Klassen-Tausch gesetzt.
  const buildWires = useCallback(() => {
    const svg = svgRef.current;
    const inner = innerRef.current;
    if (!svg || !inner) return;

    const zoom = parseFloat(inner.style.zoom) || 1;
    const w = inner.offsetWidth;
    const h = inner.offsetHeight;
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    svg.style.width = w + "px";
    svg.style.height = h + "px";

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const iRect = inner.getBoundingClientRect();
    const coord = (rect, side) => ({
      x: (side === "right" ? rect.right - iRect.left : rect.left - iRect.left) / zoom,
      y: (rect.top + rect.height / 2 - iRect.top) / zoom,
    });

    // Karten-Elemente + ihre Rects einmal cachen (erste DOM-Reihenfolge =
    // identisch zum bisherigen querySelector-Verhalten).
    const cardEls = {};
    inner.querySelectorAll("[data-card-id]").forEach(el => {
      const k = el.getAttribute("data-card-id");
      if (!(k in cardEls)) cardEls[k] = el;
    });
    const rectCache = new Map();
    const rectOf = (el) => {
      let r = rectCache.get(el);
      if (!r) { r = el.getBoundingClientRect(); rectCache.set(el, r); }
      return r;
    };

    // Ruhelinien nur bei kleinen Topologien animieren.
    const animate = scenarios.length <= FLOW_ANIM_MAX;
    const pidCounts = stats?.pidCounts || {};
    const frag = document.createDocumentFragment();

    const drawLine = (from, to, seg, hasErr, scId) => {
      const cpx = (to.x - from.x) * 0.4;
      const d = `M${from.x},${from.y} C${from.x + cpx},${from.y} ${to.x - cpx},${to.y} ${to.x},${to.y}`;
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      path.setAttribute("class", `iio-pip-wire ${seg}${hasErr ? " werr" : ""}${animate ? " anim" : ""}`);
      path.dataset.sc = scId;
      frag.appendChild(path);
    };

    scenarios.forEach(sc => {
      if (focusMode && focusedCard) {
        const isRelevant =
          focusedCard.id === sc.id ||
          (focusedCard.type === "sender" && sc.senderSystemId === focusedCard.id) ||
          (focusedCard.type === "receiver" && sc.receiverSystemId === focusedCard.id);
        if (!isRelevant) return;
      }

      const scCard = cardEls[sc.id];
      if (!scCard) return;
      const scRect = rectOf(scCard);
      const scLeft = coord(scRect, "left");
      const scRight = coord(scRect, "right");
      const hasErr = (pidCounts[sc.pid]?.failed || 0) > 0;

      // Sender System - Szenario
      if (sc.senderSystemId && cardEls[sc.senderSystemId]) {
        const ssR = coord(rectOf(cardEls[sc.senderSystemId]), "right");
        drawLine(ssR, scLeft, "src", hasErr, sc.id);
      }

      // Szenario - Receiver System
      if (sc.receiverSystemId && cardEls[sc.receiverSystemId]) {
        const rsL = coord(rectOf(cardEls[sc.receiverSystemId]), "left");
        drawLine(scRight, rsL, "dst", hasErr, sc.id);
      }
    });

    svg.appendChild(frag);
    // Aktuellen Hover-/Highlight-Zustand auf die frischen Pfade anwenden.
    applyInteractionStatesRef.current?.();
  }, [scenarios, focusMode, focusedCard, stats]);
  buildWiresRef.current = buildWires;

  // === Hover/Highlight: rein per DOM-Klassen (KEIN React-Re-Render) ===
  // Setzt Karten- (hl/on) und Linien-Zustand (hot/mute). Die Abdunkelung der
  // uebrigen Karten macht CSS ueber die Container-Klasse .has-active - so muss
  // nicht jede der u.U. hunderten Karten einzeln angefasst werden.
  const applyInteractionStates = useCallback(() => {
    const inner = innerRef.current;
    const svg = svgRef.current;
    const sel = (type, id) => inner && inner.querySelector(`[data-card-id="${id}"][data-card-type="${type}"]`);
    const wanted = highlightRef.current || hoverRef.current;
    // Zeigt der Verweis auf eine (z.B. weggefilterte) Karte ohne DOM-Knoten,
    // behandeln wir ihn als inaktiv - sonst blieben alle anderen Karten grundlos abgedunkelt.
    const activeEl = wanted ? sel(wanted.type, wanted.id) : null;
    const active = activeEl ? wanted : null;

    if (inner) {
      const cols = inner.querySelector(".iio-pip-cols");
      // Nur die wenigen bisher markierten Karten zuruecksetzen.
      inner.querySelectorAll(".iio-pip-card.hl, .iio-pip-card.on").forEach(c => c.classList.remove("hl", "on"));
      if (!active) {
        cols && cols.classList.remove("has-active");
      } else {
        cols && cols.classList.add("has-active");
        activeEl.classList.add("hl");
        // Verbundene Karten direkt als DOM-Referenzen sammeln (O(connected)).
        const onEls = new Set();
        const addOn = (type, id) => { const el = sel(type, id); if (el) onEls.add(el); };
        scenarios.forEach(sc => {
          let rel = false;
          if (active.type === "sender" && sc.senderSystemId === active.id) rel = true;
          else if (active.type === "receiver" && sc.receiverSystemId === active.id) rel = true;
          else if (active.type === "scenario" && sc.id === active.id) rel = true;
          if (!rel) return;
          addOn("scenario", sc.id);
          if (sc.senderSystemId) addOn("sender", sc.senderSystemId);
          if (sc.receiverSystemId) addOn("receiver", sc.receiverSystemId);
        });
        onEls.delete(activeEl);
        onEls.forEach(el => { if (!el.classList.contains("hl")) el.classList.add("on"); });
      }
    }

    if (svg) {
      const paths = svg.querySelectorAll(".iio-pip-wire");
      if (!active) {
        paths.forEach(p => p.classList.remove("hot", "mute"));
      } else {
        const hotIds = new Set();
        scenarios.forEach(sc => {
          if (active.id === sc.id || active.id === sc.senderSystemId || active.id === sc.receiverSystemId) hotIds.add(sc.id);
        });
        paths.forEach(p => {
          if (hotIds.has(p.dataset.sc)) { p.classList.add("hot"); p.classList.remove("mute"); }
          else { p.classList.add("mute"); p.classList.remove("hot"); }
        });
      }
    }
  }, [scenarios]);
  applyInteractionStatesRef.current = applyInteractionStates;

  // Hover/Klick setzen die Refs und triggern den billigen DOM-Tausch.
  const handleCardHover = useCallback((card) => {
    hoverRef.current = card;
    applyInteractionStatesRef.current?.();
  }, []);
  const handleCardHighlight = useCallback((card) => {
    highlightRef.current = card;
    applyInteractionStatesRef.current?.();
  }, []);

  // Geometrie neu bauen bei Daten-/Layout-/Focus-Aenderung (selten)
  useEffect(() => { buildWires(); }, [buildWires]);

  // Nach jedem (seltenen) Re-Render die DOM-Klassen erneut anwenden, da React
  // beim Reconcile die Karten-className auf die Basis zuruecksetzt.
  useEffect(() => { applyInteractionStatesRef.current?.(); });

  // Bei Fenster-Resize Geometrie aktualisieren
  useEffect(() => {
    const onResize = () => buildWiresRef.current?.();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // === Fit-to-View: Automatisches Zoom ===
  useEffect(() => {
    const inner = innerRef.current;
    const canvas = canvasRef.current;
    if (!inner || !canvas || !topology) return;

    inner.style.zoom = "1";

    const timer = setTimeout(() => {
      const naturalHeight = inner.scrollHeight;
      const availableHeight = canvas.clientHeight;

      let zoom = 1;
      if (naturalHeight > availableHeight && naturalHeight > 0) {
        zoom = availableHeight / naturalHeight;
        zoom = Math.max(zoom, 0.35);
      }

      inner.style.zoom = String(zoom);

      requestAnimationFrame(() => buildWiresRef.current?.());
    }, 50);

    return () => clearTimeout(timer);
  }, [topology, senderSystems, scenarios, receiverSystems, columnSearch]);

  // Verbindungslinien nach Focus-Animation neu positionieren
  useEffect(() => {
    const timer = setTimeout(() => buildWiresRef.current?.(), 400);
    return () => clearTimeout(timer);
  }, [focusedCard]);

  // Waehrend des Scrollens Hover auf den Karten aussetzen: sonst wandern bei
  // vielen Eintraegen die Karten unter dem Cursor durch und feuern lawinenartig
  // mouseenter/leave. pointer-events:none (per CSS-Klasse) unterbindet das.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Handle bewusst nicht "t": das wuerde die Uebersetzungsfunktion verdecken.
    let ruhe;
    const onScroll = () => {
      canvas.classList.add("iio-pip-scrolling");
      clearTimeout(ruhe);
      ruhe = setTimeout(() => canvas.classList.remove("iio-pip-scrolling"), 140);
    };
    canvas.addEventListener("scroll", onScroll, { passive: true });
    return () => { canvas.removeEventListener("scroll", onScroll); clearTimeout(ruhe); };
  }, []);

  // === Callbacks ===
  // opts.tab: Reiter, auf dem das Panel oeffnen soll (3 = Monitoring/Prozessdurchlaeufe).
  // Ein Klick auf die Zahl der Karte soll direkt dorthin fuehren, nicht auf die Uebersicht.
  const handleConnectionClick = useCallback((conn, opts) => {
    setSelectedConnection(conn);
    setDetailOpen(true);
    setDetailStartTab(opts && Number.isInteger(opts.tab) ? opts.tab : 0);
    setDetailNurFehler(!!(opts && opts.nurFehler));
  }, []);

  // Ref synchron halten, damit die Hitbox-Listener immer den aktuellen Handler nutzen
  handleConnectionClickRef.current = handleConnectionClick;

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    setSelectedConnection(null);
  }, []);

  // === Render ===
  const scopeLabel = scopeHours === 72 ? t("zeitraum.tage", { n: 3 })
    : scopeHours === 168 ? t("zeitraum.tage", { n: 7 })
    : scopeHours === 720 ? t("zeitraum.tage", { n: 30 })
    : t("zeitraum.stunden", { n: scopeHours });
  return (
    <div className={`iio-pip-root${stats ? " iio-pip-statsin" : ""}`}>
      {/* Toolbar */}
      <Toolbar
        activeFilter={activeFilter} setActiveFilter={setActiveFilter}
        sortMode={sortMode} setSortMode={setSortMode}
        spaces={spaces} selectedSpace={selectedSpace} setSelectedSpace={setSelectedSpace}
        spaceInstances={spaceInstances} selectedInst={selectedInst} setSelectedInst={setSelectedInst}
        focusMode={focusMode} setFocusMode={setFocusMode}
        showUnused={showUnused} setShowUnused={setShowUnused}
        scopeHours={scopeHours} setScopeHours={setScopeHours}
        loading={loading}
      />

      {/* Freshness-Indikatoren - nur bei aktivem experimentalCaching */}
      {isTopoOptimized && topology && (
        <div className="iio-pip-cacherow">
          <span style={{ opacity: 0.6 }}>{t("cache.titel")}</span>
          <span>{t("cache.topologie")}<FreshnessIndicator dataUpdatedAt={topoUpdatedAt} /></span>
          <span>{t("cache.stats")}<FreshnessIndicator dataUpdatedAt={statsUpdatedAt} /></span>
          <span>{t("cache.instanzen")}<FreshnessIndicator dataUpdatedAt={instancesUpdatedAt} /></span>
        </div>
      )}

      {/* Canvas-Bereich */}
      <div ref={canvasRef} className="iio-pip-canvas"
        onClick={() => { highlightRef.current = null; applyInteractionStatesRef.current?.(); if (focusMode) setFocusedCard(null); }}>
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {!loading && !error && topology && (topology.scenarios || []).length === 0 && scenarios.length === 0 && (() => {
          const diag = topology.diagnostic || {};
          const total = diag.totalPartners ?? 0;
          const pipeline = diag.pipelinePartners ?? 0;
          const criteria = (diag.detectionCriteria || []).join(", ") || "-";
          const samples = (diag.sampleNonPipeline || []).slice(0, 6);
          return (
            <div className="iio-pip-diag">
              <div className="box">
                <h2>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" stroke="var(--iio-signal)" strokeWidth="1.8" fill="none" />
                    <line x1="12" y1="8" x2="12" y2="13" stroke="var(--iio-signal)" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="16.5" r="1" fill="var(--iio-signal)" />
                  </svg>
                  {t("diagnose.titel")}
                </h2>
                <p>
                  <b style={{ color: "var(--iio-text-1)" }}>{total}</b>{t("diagnose.eintraege", { count: total })}
                  <b style={{ color: pipeline > 0 ? "var(--iio-success)" : "var(--iio-critical)" }}>{pipeline}</b>{t("diagnose.erkannt")}
                </p>
                <p><b style={{ color: "var(--iio-text-1)", fontWeight: 500 }}>{t("diagnose.kriterien")}</b> {criteria}</p>
                {samples.length > 0 && (
                  <p>
                    <b style={{ color: "var(--iio-text-1)", fontWeight: 500 }}>{t("diagnose.beispiele")}</b>{" "}
                    {samples.join(", ")}
                    {(diag.sampleNonPipeline || []).length > 6 ? " …" : ""}
                  </p>
                )}
                <div className="tip">
                  <b style={{ color: "var(--iio-text-2)" }}>{t("diagnose.tipp")}</b>{" "}
                  {t("diagnose.tippText")}
                </div>
              </div>
            </div>
          );
        })()}
        {!loading && !error && topology && scenarios.length > 0 && (
          <div ref={innerRef} className="iio-pip-inner">
            <ColumnsView
              senderSystems={senderSystems} scenarios={scenarios} receiverSystems={receiverSystems}
              onCardHover={handleCardHover} onCardHighlight={handleCardHighlight}
              onScenarioClick={handleConnectionClick}
              focusMode={focusMode} focusedCard={focusedCard} setFocusedCard={setFocusedCard}
              columnSearch={columnSearch} setColumnSearch={setColumnSearch}
              stats={stats} scopeLabel={scopeLabel}
            />
            <svg ref={svgRef} className="iio-pip-wires" />
          </div>
        )}
        {!loading && !error && !topology && (
          <div className="iio-pip-center">
            {!selectedSpace ? t("zustand.waehleSpace")
              : !selectedInst ? t("zustand.waehleInstanz")
              : t("zustand.laedtTopologie")}
          </div>
        )}
      </div>

      {/* Fußzeile */}
      <Footer stats={stats} connections={scenarios} />

      {/* Detail-Panel (Drawer) */}
      {detailOpen && selectedConnection && (
        <DetailPanel
          connection={selectedConnection}
          topology={topology}
          stats={stats}
          scopeLabel={scopeLabel}
          scopeHours={scopeHours}
          onClose={handleDetailClose}
          instanceId={selectedInst?.id}
          selectedInst={selectedInst}
          onNavigate={onNavigate}
          selectedSpace={selectedSpace}
          startTab={detailStartTab}
          startNurFehler={detailNurFehler}
        />
      )}
    </div>
  );
}
